import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PosDeviceContext } from '../context/PosDeviceContext';
import Navbar from './Navbar';
import AppIcon from './icons/AppIcon';
import api, { getImageUrl } from '../config/api';
import './PosHub.css';
import './HubShell.css';
import { PosCakeWizard, PosCakeOrdersTab } from './PosCakeOrder';
import { formatDateDisplay, formatDateTimeDisplay } from '../utils/formatDate';

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n) || 0);

const PAYMENT = ['Cash', 'Card', 'UPI', 'Other'];
const PAYMENT_OTHER_OPTIONS = [
  'Friends and family',
  'Staff meal',
  'Complimentary',
  'Marketing / promo',
  'Other'
];
const CAKE_WEIGHT_OPTIONS = Array.from({ length: 60 }, (_, i) => Math.round((i + 1) * 0.5 * 100) / 100); // 0.5 … 30

/** Whole-cake tap category (not slice cakes / muffins). */
function isCakeTapCategory(cat) {
  if (!cat || cat.sale_mode !== 'select') return false;
  const n = String(cat.name || '')
    .trim()
    .toLowerCase();
  return n === 'cake' || n === 'cakes' || n === 'whole cake' || n === 'whole cakes';
}

/** Auto sign-out when the cashier is idle (no mouse / keyboard / touch / scroll). */
const POS_IDLE_LOGOUT_MS = 5 * 60 * 1000;

function formatPaymentDisplay(sale, payments) {
  if (!sale && !payments) return '';
  const parts = Array.isArray(payments) && payments.length
    ? payments
    : Array.isArray(sale?.payments) && sale.payments.length
      ? sale.payments
      : null;
  if (parts && parts.length > 1) {
    return parts
      .map((p) => {
        const label =
          p.payment_method === 'Other' && p.payment_other_label
            ? `Other (${p.payment_other_label})`
            : p.payment_method;
        return `${label} ${formatInr(p.amount)}`;
      })
      .join(' + ');
  }
  if (parts && parts.length === 1) {
    const p = parts[0];
    if (p.payment_method === 'Other') {
      return p.payment_other_label ? `Other (${p.payment_other_label})` : 'Other';
    }
    return p.payment_method;
  }
  const pm = sale?.payment_method || '';
  if (pm === 'Split') return 'Split';
  if (pm !== 'Other') return pm;
  const sub = sale?.payment_other_label;
  return sub ? `Other (${sub})` : 'Other';
}

const SOURCES = [
  { value: 'catering_inquiry', label: 'Catering inquiry' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'phone', label: 'Phone' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' }
];
const STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost'];

function isReadyLine(line) {
  const desc = String(line?.description || '').trim();
  const rawPrice = line?.unitPrice;
  if (!desc) return false;
  if (rawPrice === '' || rawPrice == null) return false;
  const unit = Number(rawPrice);
  return Number.isFinite(unit) && unit > 0;
}

function addLineToBill(setLines, description, unitPrice, qty = 1, meta = null) {
  const price = Math.round(Number(unitPrice) * 100) / 100;
  const desc = String(description || '').trim();
  const productId = meta?.productId != null ? Number(meta.productId) : null;
  const categoryId = meta?.categoryId != null ? Number(meta.categoryId) : null;
  setLines((prev) => {
    const idx = prev.findIndex((l) => {
      if (productId && l.productId) {
        return Number(l.productId) === productId && Number(l.unitPrice) === price;
      }
      return String(l.description).trim() === desc && Number(l.unitPrice) === price && !l.productId;
    });
    if (idx >= 0) {
      return prev.map((l, i) => (i === idx ? { ...l, qty: (Number(l.qty) || 0) + qty } : l));
    }
    const emptyIdx = prev.findIndex((l) => !String(l.description || '').trim());
    const nextLine = {
      description: desc,
      qty,
      unitPrice: price,
      ...(productId ? { productId } : {}),
      ...(categoryId ? { categoryId } : {})
    };
    if (emptyIdx >= 0) {
      return prev.map((l, i) => (i === emptyIdx ? nextLine : l));
    }
    return [...prev, nextLine];
  });
}

function productTracksStock(p) {
  return p && Number(p.track_stock) !== 0 && p.track_stock !== false;
}

function productStockQty(p) {
  return Math.max(0, parseFloat(p?.stock_qty) || 0);
}

function productIsOutOfStock(p) {
  return productTracksStock(p) && productStockQty(p) <= 0;
}

export default function PosHub() {
  const { user, logout } = useContext(AuthContext);
  const { device: posDevice, enrolled: posDeviceEnrolled } = useContext(PosDeviceContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'Admin';
  const isStoreStaff = ['Admin', 'Store Manager', 'Cashier', 'Franchise Admin'].includes(user?.role);
  const isOrderCreator = user?.role === 'Order Creator';
  const showCrmLeads = user?.role !== 'Cashier';
  const adminLivePreview = isAdmin && !posDeviceEnrolled;

  const [tab, setTab] = useState(isOrderCreator ? 'leads' : 'register');
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentOtherSubtype, setPaymentOtherSubtype] = useState(PAYMENT_OTHER_OPTIONS[0]);
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState({ Cash: '', Card: '', UPI: '', Other: '' });
  const [splitOtherSubtype, setSplitOtherSubtype] = useState(PAYMENT_OTHER_OPTIONS[0]);
  const [cartDiscount, setCartDiscount] = useState('');
  const [lines, setLines] = useState([]);
  const [msg, setMsg] = useState(null);
  const [dayCloseOpen, setDayCloseOpen] = useState(false);
  const [dayCloseLoading, setDayCloseLoading] = useState(false);
  const [dayCloseSending, setDayCloseSending] = useState(false);
  const [dayCloseData, setDayCloseData] = useState(null);
  const [sales, setSales] = useState([]);
  const [cakeCollections, setCakeCollections] = useState([]);
  const [salesTopItems, setSalesTopItems] = useState([]);
  const [salesReportSummary, setSalesReportSummary] = useState({
    total: 0,
    cash: 0,
    card: 0,
    upi: 0,
    others: 0,
    cancelled: 0,
    posTotal: 0,
    cakeTotal: 0,
    cakeAdvanceTotal: 0,
    cakeSaleTotal: 0,
    saleCount: 0,
    cancelledCount: 0,
    cakeCount: 0,
    cakeAdvanceCount: 0,
    cakeSaleCount: 0,
    ticketCount: 0
  });
  const todayIsoLocal = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [salesFrom, setSalesFrom] = useState(todayIsoLocal);
  const [salesTo, setSalesTo] = useState(todayIsoLocal);
  const [salesPaymentFilter, setSalesPaymentFilter] = useState('all');
  const [salesProductFilter, setSalesProductFilter] = useState('all');
  const [salesListFilter, setSalesListFilter] = useState('all'); // all | completed | cancelled
  const [loadingSales, setLoadingSales] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expensesSummary, setExpensesSummary] = useState({
    count: 0,
    totalAmount: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    rejectedAmount: 0
  });
  const [expensesFrom, setExpensesFrom] = useState(todayIsoLocal);
  const [expensesTo, setExpensesTo] = useState(todayIsoLocal);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [expenseSubmitting, setExpenseSubmitting] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    employeeName: '',
    reason: '',
    expenseDate: new Date().toISOString().slice(0, 10),
    amount: '',
    paymentMethod: 'Cash',
    receiptFiles: []
  });
  const emptyExpenseForm = () => ({
    employeeName: '',
    reason: '',
    expenseDate: new Date().toISOString().slice(0, 10),
    amount: '',
    paymentMethod: 'Cash',
    receiptFiles: []
  });
  const [pickupSummary, setPickupSummary] = useState({
    todayPickedUp: 0,
    todayPickupTotal: 0,
    tomorrowTotal: 0,
    futureTotal: 0
  });

  const [leadForm, setLeadForm] = useState({
    storeId: '',
    contactName: '',
    phone: '',
    email: '',
    company: '',
    source: 'catering_inquiry',
    notes: ''
  });
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const [newStore, setNewStore] = useState({
    code: '',
    name: '',
    city: 'Hyderabad',
    phone: '',
    address: '',
    gstin: ''
  });
  const [posKpi, setPosKpi] = useState(null);
  const [scannerInput, setScannerInput] = useState('');
  const [lastScan, setLastScan] = useState(null);
  const [shortcodeMap] = useState(() => {
    try {
      const raw = localStorage.getItem('posShortcodeMap');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const scannerRef = useRef(null);
  const posIdleTimerRef = useRef(null);

  const [catalogCategories, setCatalogCategories] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  const [menuNewCat, setMenuNewCat] = useState({ name: '', sale_mode: 'barcode' });
  const [menuEditModalOpen, setMenuEditModalOpen] = useState(false);
  const [menuEditForm, setMenuEditForm] = useState({
    id: '',
    categoryId: '',
    name: '',
    description: '',
    weight: '',
    unitPrice: '',
    shortcode: '',
    image_url: ''
  });

  const [saleDetailOpen, setSaleDetailOpen] = useState(false);
  const [saleDetailLoading, setSaleDetailLoading] = useState(false);
  const [saleDetailSale, setSaleDetailSale] = useState(null);
  const [saleDetailLines, setSaleDetailLines] = useState([]);
  const [saleDetailPayments, setSaleDetailPayments] = useState([]);

  const [menuAddModalOpen, setMenuAddModalOpen] = useState(false);
  const [menuAddForm, setMenuAddForm] = useState({
    categoryId: '',
    name: '',
    description: '',
    weight: '',
    unitPrice: '',
    shortcode: '',
    image_url: ''
  });

  const [productFilter, setProductFilter] = useState('');
  const [lastReceipt, setLastReceipt] = useState(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptWaPhone, setReceiptWaPhone] = useState('');
  const [receiptWaSending, setReceiptWaSending] = useState(false);
  const [receiptWaStatus, setReceiptWaStatus] = useState(null);
  /** Keep the loaded store menu ready as soon as the cashier reaches POS. */
  const [registerSessionActive, setRegisterSessionActive] = useState(true);
  const [cakeWizardOpen, setCakeWizardOpen] = useState(false);
  const [cakeFlavours, setCakeFlavours] = useState([]);
  const [cakePriceRows, setCakePriceRows] = useState([]);
  const [cakeWeightModal, setCakeWeightModal] = useState(null);
  const [cakeWeight, setCakeWeight] = useState(1);
  const [cakePrice, setCakePrice] = useState('');
  const [cakePriceLoading, setCakePriceLoading] = useState(false);
  const [productWeightModal, setProductWeightModal] = useState(null);
  const [productWeightIdx, setProductWeightIdx] = useState(0);
  const [openItemModalOpen, setOpenItemModalOpen] = useState(false);
  const [openItemForm, setOpenItemForm] = useState({ name: '', price: '', qty: '1' });

  const orderCategoryForPos = useMemo(() => {
    const s = stores.find((x) => String(x.id) === String(storeId));
    if (!s) return 'Store-1';
    const code = String(s.code || '').trim().toUpperCase();
    const name = String(s.name || '').trim().toUpperCase();
    const codeNum = code.match(/(\d+)\s*$/);
    if (
      code === '2' ||
      code === 'STORE-2' ||
      (codeNum && codeNum[1] === '2') ||
      name.includes('STORE-2') ||
      name.includes('BRANCH-2') ||
      Number(s.id) === 2
    ) {
      return 'Store-2';
    }
    return 'Store-1';
  }, [stores, storeId]);

  useEffect(() => {
    if (!isStoreStaff) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: sd }, { data: ps }] = await Promise.all([
          api.get('/orders/server-date'),
          api.get('/pos/sales', { params: { limit: 400 } })
        ]);
        const day = sd?.serverDate || '';
        const list = ps?.sales || [];
        const todaySales = list.filter(
          (s) => !s.cancelled_at && day && String(s.created_at).slice(0, 10) === day
        );
        const todayRevenue = todaySales.reduce((a, s) => a + parseFloat(s.subtotal || 0), 0);
        if (!cancelled) {
          setPosKpi({ serverDate: day, todayCount: todaySales.length, todayRevenue, recentCount: list.length });
          if (day) {
            setSalesFrom(day);
            setSalesTo(day);
          }
        }
      } catch {
        if (!cancelled) setPosKpi(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStoreStaff]);

  const loadStores = useCallback(async () => {
    try {
      const { data } = await api.get('/stores');
      let list = data.stores || [];
      const assigned = user?.assigned_stores;
      if (user?.role !== 'Admin' && Array.isArray(assigned) && assigned.length > 0) {
        const allowed = new Set(assigned.map((s) => s.id));
        list = list.filter((s) => allowed.has(s.id));
      }
      if (posDevice?.store_id) {
        list = list.filter((s) => Number(s.id) === Number(posDevice.store_id));
      }
      setStores(list);
      const queryStoreId = searchParams.get('storeId');
      setStoreId((prev) => {
        const fromQuery =
          queryStoreId && list.some((s) => String(s.id) === String(queryStoreId))
            ? String(queryStoreId)
            : null;
        const pref = posDevice?.store_id || fromQuery || user?.active_store_id;
        if (pref && list.some((s) => String(s.id) === String(pref))) {
          return String(pref);
        }
        return prev || (list[0] ? String(list[0].id) : '');
      });
      setLeadForm((f) => {
        const fromQuery =
          queryStoreId && list.some((s) => String(s.id) === String(queryStoreId))
            ? String(queryStoreId)
            : null;
        const pref = posDevice?.store_id || fromQuery || user?.active_store_id;
        const sid =
          pref && list.some((s) => String(s.id) === String(pref))
            ? String(pref)
            : list[0]
              ? String(list[0].id)
              : '';
        return { ...f, storeId: f.storeId || sid };
      });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Could not load stores. Run DB migration on server.' });
    }
  }, [user?.role, user?.assigned_stores, user?.active_store_id, posDevice?.store_id, searchParams]);

  const loadSales = useCallback(async () => {
    setLoadingSales(true);
    try {
      const from = salesFrom || todayIsoLocal();
      const to = salesTo || from;
      const sid = parseInt(storeId, 10);
      const { data } = await api.get('/pos/sales-report', {
        params: {
          limit: 400,
          from,
          to: to < from ? from : to,
          storeId: sid || undefined,
          paymentMode: salesPaymentFilter || 'all',
          productType: salesProductFilter || 'all'
        }
      });
      setSales(data.sales || []);
      setCakeCollections(data.cakeCollections || []);
      setSalesTopItems(data.topItems || []);
      setSalesReportSummary(
        data.summary || {
          total: 0,
          cash: 0,
          card: 0,
          upi: 0,
          others: 0,
          cancelled: 0,
          posTotal: 0,
          cakeTotal: 0,
          cakeAdvanceTotal: 0,
          cakeSaleTotal: 0,
          saleCount: 0,
          cancelledCount: 0,
          cakeCount: 0,
          cakeAdvanceCount: 0,
          cakeSaleCount: 0,
          ticketCount: 0
        }
      );
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Could not load POS sales' });
    } finally {
      setLoadingSales(false);
    }
  }, [salesFrom, salesTo, storeId, salesPaymentFilter, salesProductFilter]);

  const loadLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const { data } = await api.get('/crm/leads', { params: { limit: 100 } });
      setLeads(data.leads || []);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Could not load leads' });
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoadingExpenses(true);
    try {
      const sid = parseInt(storeId, 10);
      const from = expensesFrom || todayIsoLocal();
      const to = expensesTo || from;
      const { data } = await api.get('/pos/expenses', {
        params: {
          limit: 300,
          storeId: sid || undefined,
          startDate: from,
          endDate: to
        }
      });
      setExpenses(data.expenses || []);
      setExpensesSummary(
        data.summary || {
          count: (data.expenses || []).length,
          totalAmount: (data.expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
          pendingAmount: 0,
          approvedAmount: 0,
          rejectedAmount: 0
        }
      );
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Could not load expenses' });
    } finally {
      setLoadingExpenses(false);
    }
  }, [storeId, expensesFrom, expensesTo]);

  const loadCatalog = useCallback(async () => {
    const sid = parseInt(storeId, 10);
    if (!sid) {
      setCatalogCategories([]);
      setActiveCategoryId(null);
      return;
    }
    setCatalogLoading(true);
    try {
      const { data } = await api.get('/pos/catalog', { params: { storeId: sid } });
      const cats = data.categories || [];
      setCatalogCategories(cats);
      setActiveCategoryId((prev) => {
        if (prev && cats.some((c) => c.id === prev)) return prev;
        return cats[0]?.id ?? null;
      });
    } catch {
      setCatalogCategories([]);
      setActiveCategoryId(null);
    } finally {
      setCatalogLoading(false);
    }
  }, [storeId]);

  const loadCakeFlavours = useCallback(async () => {
    const sid = parseInt(storeId, 10);
    if (!sid || !isStoreStaff) {
      setCakeFlavours([]);
      setCakePriceRows([]);
      return;
    }
    try {
      const [{ data: flavourData }, { data: priceData }] = await Promise.all([
        api.get('/users/flavours'),
        api.get('/pos/cake-prices/catalog', { params: { storeId: sid } })
      ]);
      setCakeFlavours(flavourData.flavours || []);
      setCakePriceRows(priceData.rows || []);
    } catch {
      setCakeFlavours([]);
      setCakePriceRows([]);
    }
  }, [storeId, isStoreStaff]);

  const barcodeIndex = useMemo(() => {
    const idx = {};
    for (const c of catalogCategories) {
      for (const p of c.products || []) {
        const code = p.shortcode != null ? String(p.shortcode).trim() : '';
        if (!code) continue;
        const variants = Array.isArray(p.variants) ? p.variants : [];
        const price =
          variants.length > 0
            ? Number(variants[0].unit_price)
            : parseFloat(p.unit_price) || 0;
        idx[code] = {
          name: p.name,
          price,
          productId: p.id,
          categoryId: p.category_id || c.id,
          weight: variants[0]?.weight || p.weight || null,
          stock_qty: p.stock_qty,
          track_stock: p.track_stock
        };
      }
    }
    return idx;
  }, [catalogCategories]);

  // Every category is click-to-sell; barcode is optional per item via shortcode
  const tapCategories = useMemo(() => catalogCategories, [catalogCategories]);

  const barcodeCategories = useMemo(
    () =>
      catalogCategories.filter((c) =>
        (c.products || []).some((p) => p.shortcode != null && String(p.shortcode).trim() !== '')
      ),
    [catalogCategories]
  );

  const menuAddSelectedCat = useMemo(() => {
    const id = parseInt(menuAddForm.categoryId, 10);
    if (!id) return null;
    return catalogCategories.find((c) => c.id === id) || null;
  }, [catalogCategories, menuAddForm.categoryId]);

  const menuEditSelectedCat = useMemo(() => {
    const id = parseInt(menuEditForm.categoryId, 10);
    if (!id) return null;
    return catalogCategories.find((c) => c.id === id) || null;
  }, [catalogCategories, menuEditForm.categoryId]);

  const activeCategory = useMemo(
    () => catalogCategories.find((c) => c.id === activeCategoryId) || null,
    [catalogCategories, activeCategoryId]
  );

  const cakeCategoryActive = useMemo(() => isCakeTapCategory(activeCategory), [activeCategory]);

  const cakePricesByFlavour = useMemo(() => {
    const map = {};
    for (const row of cakePriceRows) {
      const key = String(row.flavour_key || '')
        .trim()
        .toUpperCase();
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push({
        weight: Number(row.weight_kg),
        price: Number(row.price)
      });
    }
    return map;
  }, [cakePriceRows]);

  const filteredProducts = useMemo(() => {
    const prods = activeCategory?.products || [];
    const q = productFilter.trim().toLowerCase();
    if (!q) return prods;
    return prods.filter((p) => String(p.name || '').toLowerCase().includes(q));
  }, [activeCategory, productFilter]);

  const filteredCakeFlavours = useMemo(() => {
    const q = productFilter.trim().toLowerCase();
    if (!q) return cakeFlavours;
    return cakeFlavours.filter((f) => String(f.name || '').toLowerCase().includes(q));
  }, [cakeFlavours, productFilter]);

  const cakeWeightOptions = useMemo(() => {
    if (!cakeWeightModal) return CAKE_WEIGHT_OPTIONS;
    const key = String(cakeWeightModal.name || '')
      .trim()
      .toUpperCase();
    const priced = (cakePricesByFlavour[key] || [])
      .map((r) => r.weight)
      .filter((w) => Number.isFinite(Number(w)))
      .map((w) => Number(w))
      .sort((a, b) => a - b);
    // Prefer configured store prices; otherwise full dropdown range
    if (priced.length) return [...new Set(priced)];
    return CAKE_WEIGHT_OPTIONS;
  }, [cakeWeightModal, cakePricesByFlavour]);

  const cartGross = useMemo(() => {
    let s = 0;
    for (const l of lines) {
      if (!isReadyLine(l)) continue;
      const qty = Number(l.qty) || 0;
      const unit = Number(l.unitPrice);
      if (!Number.isFinite(unit)) continue;
      s += qty * unit;
    }
    return Math.round(s * 100) / 100;
  }, [lines]);

  const cartDiscountRupees = useMemo(() => {
    const raw = parseInt(String(cartDiscount).replace(/\D/g, ''), 10);
    if (!Number.isFinite(raw) || raw < 0) return 0;
    return Math.min(raw, Math.floor(cartGross));
  }, [cartGross, cartDiscount]);

  const cartTotal = useMemo(
    () => Math.max(0, Math.round((cartGross - cartDiscountRupees) * 100) / 100),
    [cartGross, cartDiscountRupees]
  );

  const displayName = user?.full_name?.trim() || user?.username || 'User';

  const posTopbarStoreLabel = useMemo(() => {
    const sid = parseInt(storeId, 10);
    if (sid) {
      const hit = stores.find((x) => Number(x.id) === sid);
      if (hit?.name) return hit.name;
    }
    const as = user?.assigned_stores;
    if (as?.length === 1) return as[0].name;
    if (as?.length > 1) return as.map((x) => x.name).join(' · ');
    return null;
  }, [storeId, stores, user?.assigned_stores]);

  const salesSummary = salesReportSummary;

  const visibleSales = useMemo(() => {
    const list = sales || [];
    if (salesListFilter === 'cancelled') return list.filter((s) => s.cancelled_at);
    if (salesListFilter === 'completed') return list.filter((s) => !s.cancelled_at);
    return list;
  }, [sales, salesListFilter]);

  const showCancelledOnly = salesListFilter === 'cancelled';

  const cakeAdvanceRows = useMemo(
    () => (cakeCollections || []).filter((c) => c.collectionKind !== 'sale'),
    [cakeCollections]
  );
  const cakeSaleRows = useMemo(
    () => (cakeCollections || []).filter((c) => c.collectionKind === 'sale'),
    [cakeCollections]
  );

  const showCancelledSalesList = () => {
    setSalesPaymentFilter('all');
    setSalesListFilter('cancelled');
  };

  const splitPaidTotal = useMemo(() => {
    return Math.round(
      PAYMENT.reduce((sum, method) => sum + (Number(splitAmounts[method]) || 0), 0) * 100
    ) / 100;
  }, [splitAmounts]);

  const splitRemaining = useMemo(() => {
    return Math.round((Number(cartTotal) - splitPaidTotal) * 100) / 100;
  }, [cartTotal, splitPaidTotal]);

  const resetPaymentState = () => {
    setPaymentMethod('Cash');
    setPaymentOtherSubtype(PAYMENT_OTHER_OPTIONS[0]);
    setSplitPayment(false);
    setSplitAmounts({ Cash: '', Card: '', UPI: '', Other: '' });
    setSplitOtherSubtype(PAYMENT_OTHER_OPTIONS[0]);
  };

  const fillSplitRemaining = (method) => {
    const remaining = Math.max(0, splitRemaining);
    if (remaining <= 0) return;
    setSplitAmounts((prev) => {
      const current = Number(prev[method]) || 0;
      return {
        ...prev,
        [method]: String(Math.round((current + remaining) * 100) / 100)
      };
    });
  };

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    if (!showCrmLeads && tab === 'leads') setTab('register');
  }, [showCrmLeads, tab]);

  useEffect(() => {
    if (!isStoreStaff && (tab === 'cakes' || tab === 'sales' || tab === 'expenses')) setTab('register');
  }, [isStoreStaff, tab]);

  const scheduleScannerFocus = useCallback(() => {
    requestAnimationFrame(() => {
      scannerRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const handleScannerBlur = useCallback((e) => {
    if (!registerSessionActive || tab !== 'register') return;
    const next = e.relatedTarget;
    if (next && ['INPUT', 'TEXTAREA', 'SELECT'].includes(next.tagName)) return;
    setTimeout(() => {
      if (registerSessionActive && tab === 'register') {
        scannerRef.current?.focus({ preventScroll: true });
      }
    }, 0);
  }, [registerSessionActive, tab]);

  useEffect(() => {
    if (tab !== 'register' || !registerSessionActive) return;
    const id = setTimeout(() => scannerRef.current?.focus({ preventScroll: true }), 100);
    return () => clearTimeout(id);
  }, [tab, storeId, activeCategoryId, registerSessionActive]);

  useEffect(() => {
    if (tab === 'sales' && isStoreStaff) loadSales();
    if (tab === 'expenses' && isStoreStaff) loadExpenses();
    if (tab === 'leads' && showCrmLeads) loadLeads();
  }, [
    tab,
    isStoreStaff,
    loadSales,
    loadExpenses,
    loadLeads,
    showCrmLeads,
    salesFrom,
    salesTo,
    salesPaymentFilter,
    salesProductFilter,
    expensesFrom,
    expensesTo,
    storeId
  ]);

  useEffect(() => {
    if (!isStoreStaff || !parseInt(storeId, 10)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: dayData } = await api.get('/orders/server-date');
        const today = dayData?.serverDate || new Date().toISOString().slice(0, 10);
        const tmrDate = new Date(`${today}T00:00:00`);
        tmrDate.setDate(tmrDate.getDate() + 1);
        const tomorrow = tmrDate.toISOString().slice(0, 10);
        const futureEndDate = new Date(`${today}T00:00:00`);
        futureEndDate.setDate(futureEndDate.getDate() + 45);
        const endDate = futureEndDate.toISOString().slice(0, 10);
        const { data } = await api.get('/orders', {
          params: { startDate: today, endDate, category: orderCategoryForPos }
        });
        const list = data?.orders || [];
        const sid = parseInt(storeId, 10);
        let todayPickedUp = 0;
        let todayPickupTotal = 0;
        let tomorrowTotal = 0;
        let futureTotal = 0;
        list.forEach((o) => {
          const status = String(o.status || '').toLowerCase();
          if (status === 'cancelled') return;
          // Store-category OMS orders are counter pickups; POS cake delivery is not.
          const mode = String(o.pos_cake_mode || '').toLowerCase();
          if (mode === 'delivery') return;
          const oStore = Number(o.pos_store_id);
          if (sid && Number.isFinite(oStore) && oStore > 0 && oStore !== sid) return;
          const d = String(o.delivery_date || '').slice(0, 10);
          if (!d) return;
          if (d === today) {
            todayPickupTotal += 1;
            if (status === 'delivered') todayPickedUp += 1;
          } else if (d === tomorrow) {
            tomorrowTotal += 1;
          } else if (d > tomorrow) {
            futureTotal += 1;
          }
        });
        if (!cancelled) {
          setPickupSummary({ todayPickedUp, todayPickupTotal, tomorrowTotal, futureTotal });
        }
      } catch {
        if (!cancelled) {
          setPickupSummary({ todayPickedUp: 0, todayPickupTotal: 0, tomorrowTotal: 0, futureTotal: 0 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStoreStaff, storeId, orderCategoryForPos, tab]);

  useEffect(() => {
    if (!isStoreStaff) return;
    loadCatalog();
  }, [storeId, isStoreStaff, loadCatalog]);

  useEffect(() => {
    if (!isStoreStaff) return;
    loadCakeFlavours();
  }, [storeId, isStoreStaff, loadCakeFlavours]);

  useEffect(() => {
    if (!cakeWeightModal) return;
    const key = String(cakeWeightModal.name || '')
      .trim()
      .toUpperCase();
    const priced = cakePricesByFlavour[key] || [];
    const match = priced.find((r) => Number(r.weight) === Number(cakeWeight));
    if (match && Number.isFinite(match.price)) {
      setCakePrice(String(match.price));
      return;
    }
    let cancelled = false;
    (async () => {
      setCakePriceLoading(true);
      try {
        const sid = parseInt(storeId, 10);
        const { data } = await api.get('/pos/cake-prices', {
          params: { storeId: sid, flavour: cakeWeightModal.name, weightKg: cakeWeight }
        });
        if (!cancelled) {
          setCakePrice(data.price != null ? String(data.price) : '');
        }
      } catch {
        if (!cancelled) setCakePrice('');
      } finally {
        if (!cancelled) setCakePriceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cakeWeightModal, cakeWeight, cakePricesByFlavour, storeId]);

  const removeLine = (i) => {
    if (!registerSessionActive) return;
    setLines((L) => {
      const next = L.filter((_, j) => j !== i);
      return next;
    });
    scheduleScannerFocus();
  };

  const openOpenItemModal = () => {
    if (!parseInt(storeId, 10)) {
      setMsg({ type: 'error', text: 'Choose a store first' });
      return;
    }
    setTab('register');
    if (!registerSessionActive) {
      setRegisterSessionActive(true);
      setTimeout(() => scannerRef.current?.focus({ preventScroll: true }), 120);
    }
    setOpenItemForm({ name: '', price: '', qty: '1' });
    setOpenItemModalOpen(true);
  };

  const submitOpenItem = (e) => {
    e.preventDefault();
    const name = String(openItemForm.name || '').trim();
    const price = Number(openItemForm.price);
    const qty = Math.max(0.01, Number(openItemForm.qty) || 1);
    if (!name) {
      setMsg({ type: 'error', text: 'Enter an item name' });
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setMsg({ type: 'error', text: 'Enter a valid price' });
      return;
    }
    if (!registerSessionActive) {
      setRegisterSessionActive(true);
    }
    addLineToBill(setLines, name, price, qty);
    setOpenItemModalOpen(false);
    setOpenItemForm({ name: '', price: '', qty: '1' });
    setMsg({ type: 'ok', text: `Added “${name}” to cart` });
    scheduleScannerFocus();
  };

  const submitSale = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!registerSessionActive) {
      setMsg({ type: 'error', text: 'Tap New order first.' });
      return;
    }
    const sid = parseInt(storeId, 10);
    const payloadLines = lines
      .map((l) => ({
        description: String(l.description || '').trim(),
        qty: Number(l.qty) || 1,
        unitPrice: Number(l.unitPrice),
        productId: l.productId || undefined,
        categoryId: l.categoryId || undefined
      }))
      .filter((l) => l.description && Number.isFinite(l.unitPrice) && l.unitPrice > 0);
    if (!sid) {
      setMsg({ type: 'error', text: 'Choose a store' });
      return;
    }
    if (!payloadLines.length) {
      setMsg({ type: 'error', text: 'Add at least one item to the cart' });
      return;
    }

    const needByProduct = new Map();
    for (const l of payloadLines) {
      if (!l.productId) continue;
      needByProduct.set(l.productId, (needByProduct.get(l.productId) || 0) + l.qty);
    }
    if (needByProduct.size) {
      const productMap = new Map();
      for (const c of catalogCategories) {
        for (const p of c.products || []) productMap.set(Number(p.id), p);
      }
      for (const [pid, needQty] of needByProduct.entries()) {
        const p = productMap.get(Number(pid));
        if (!p || !productTracksStock(p)) continue;
        const avail = productStockQty(p);
        if (needQty > avail + 0.0001) {
          setMsg({
            type: 'error',
            text: `Insufficient stock for “${p.name}” (available ${avail}, in cart ${needQty})`
          });
          return;
        }
      }
    }

    let paymentPayload = {};
    if (splitPayment) {
      const payments = PAYMENT.map((method) => {
        const amount = Math.round((Number(splitAmounts[method]) || 0) * 100) / 100;
        if (amount <= 0) return null;
        return {
          paymentMethod: method,
          amount,
          paymentOtherLabel: method === 'Other' ? splitOtherSubtype : undefined
        };
      }).filter(Boolean);
      if (!payments.length) {
        setMsg({ type: 'error', text: 'Enter amounts for the payment methods used' });
        return;
      }
      if (Math.abs(splitRemaining) > 0.009) {
        setMsg({
          type: 'error',
          text:
            splitRemaining > 0
              ? `Split payments are short by ${formatInr(splitRemaining)}`
              : `Split payments are over by ${formatInr(Math.abs(splitRemaining))}`
        });
        return;
      }
      paymentPayload = { payments };
    } else {
      paymentPayload = {
        paymentMethod,
        paymentOtherLabel: paymentMethod === 'Other' ? paymentOtherSubtype : undefined
      };
    }

    try {
      const { data } = await api.post('/pos/sales', {
        storeId: sid,
        ...paymentPayload,
        notes: '',
        lines: payloadLines,
        discountAmount: cartDiscountRupees
      });
      setLastReceipt(data);
      setReceiptWaPhone('');
      setReceiptWaStatus(null);
      setReceiptModalOpen(true);
      setRegisterSessionActive(false);
      setLines([]);
      setCartDiscount('');
      resetPaymentState();
      loadSales();
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Save failed' });
    }
  };

  const bumpLineQty = (index, delta) => {
    if (!registerSessionActive) return;
    setLines((L) =>
      L.map((row, j) => {
        if (j !== index) return row;
        const next = Math.max(0.01, (Number(row.qty) || 1) + delta);
        return { ...row, qty: Math.round(next * 100) / 100 };
      })
    );
  };

  const closeReceiptModal = () => {
    setReceiptModalOpen(false);
    setLastReceipt(null);
    setReceiptWaPhone('');
    setReceiptWaStatus(null);
    setRegisterSessionActive(true);
    setTimeout(() => scannerRef.current?.focus({ preventScroll: true }), 120);
  };

  const sendReceiptWhatsApp = async (e) => {
    e?.preventDefault?.();
    const saleId = lastReceipt?.sale?.id;
    if (!saleId) return;
    const phone = String(receiptWaPhone || '').trim();
    if (!phone) {
      setReceiptWaStatus({ type: 'error', text: 'Enter the customer phone number' });
      return;
    }
    setReceiptWaSending(true);
    setReceiptWaStatus(null);
    try {
      const { data } = await api.post(`/pos/sales/${saleId}/share-whatsapp`, { phone });
      setReceiptWaStatus({
        type: 'ok',
        text: `Receipt sent to ${data.mobile || phone}`
      });
    } catch (err) {
      setReceiptWaStatus({
        type: 'error',
        text: err.response?.data?.error || 'Failed to send WhatsApp receipt'
      });
    } finally {
      setReceiptWaSending(false);
    }
  };

  const idleLogoutToLogin = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const bumpPosIdleTimer = useCallback(() => {
    if (posIdleTimerRef.current) {
      clearTimeout(posIdleTimerRef.current);
    }
    posIdleTimerRef.current = setTimeout(() => {
      posIdleTimerRef.current = null;
      idleLogoutToLogin();
    }, POS_IDLE_LOGOUT_MS);
  }, [idleLogoutToLogin]);

  useEffect(() => {
    bumpPosIdleTimer();
    const onActivity = () => bumpPosIdleTimer();
    window.addEventListener('mousedown', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('touchstart', onActivity);
    window.addEventListener('scroll', onActivity, true);
    return () => {
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('scroll', onActivity, true);
      if (posIdleTimerRef.current) {
        clearTimeout(posIdleTimerRef.current);
        posIdleTimerRef.current = null;
      }
    };
  }, [bumpPosIdleTimer]);

  const startRegisterSession = useCallback(() => {
    setRegisterSessionActive(true);
    setTimeout(() => scannerRef.current?.focus({ preventScroll: true }), 120);
  }, []);

  const printReceipt = () => {
    window.print();
  };

  const handlePosLogout = () => {
    idleLogoutToLogin();
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!leadForm.contactName.trim() || !leadForm.phone.trim()) {
      setMsg({ type: 'error', text: 'Name and phone are required' });
      return;
    }
    try {
      await api.post('/crm/leads', {
        ...leadForm,
        storeId: leadForm.storeId ? parseInt(leadForm.storeId, 10) : null
      });
      setMsg({ type: 'ok', text: 'Lead saved.' });
      setLeadForm((f) => ({
        ...f,
        contactName: '',
        phone: '',
        email: '',
        company: '',
        notes: ''
      }));
      loadLeads();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not save lead' });
    }
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/crm/leads/${id}`, { status });
      loadLeads();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Update failed' });
    }
  };

  const createStore = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!newStore.code.trim() || !newStore.name.trim()) {
      setMsg({ type: 'error', text: 'Store code and name required' });
      return;
    }
    try {
      await api.post('/stores', newStore);
      setMsg({ type: 'ok', text: 'Store added.' });
      setNewStore({ code: '', name: '', city: 'Hyderabad', phone: '', address: '', gstin: '' });
      loadStores();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not add store' });
    }
  };

  const applyScannedCode = (code) => {
    const fromCatalog = barcodeIndex[code];
    const legacy = shortcodeMap[code];
    const mapped = fromCatalog
      ? {
          name: fromCatalog.weight ? `${fromCatalog.name} · ${fromCatalog.weight}` : fromCatalog.name,
          price: fromCatalog.price,
          productId: fromCatalog.productId,
          categoryId: fromCatalog.categoryId,
          stock_qty: fromCatalog.stock_qty,
          track_stock: fromCatalog.track_stock
        }
      : legacy
        ? { name: legacy.name, price: legacy.price }
        : null;
    if (!mapped) {
      setMsg({
        type: 'error',
        text: `No product for shortcode ${code}. Ask Admin to enable barcode on the item under Admin → Store menus.`
      });
      return;
    }
    if (productIsOutOfStock(mapped)) {
      setMsg({ type: 'error', text: `“${fromCatalog.name}” is out of stock` });
      return;
    }
    addLineToBill(setLines, mapped.name, mapped.price, 1, {
      productId: mapped.productId,
      categoryId: mapped.categoryId
    });
    const src = fromCatalog ? 'menu' : 'device';
    setMsg({ type: 'ok', text: `Scanned ${code}: ${mapped.name} added (${src === 'menu' ? 'store menu' : 'legacy map'})` });
  };

  const tapProduct = (product) => {
    if (!registerSessionActive) return;
    if (productIsOutOfStock(product)) {
      setMsg({ type: 'error', text: `“${product.name}” is out of stock` });
      return;
    }
    const meta = {
      productId: product.id,
      categoryId: product.category_id || activeCategoryId
    };
    const variants = Array.isArray(product.variants)
      ? product.variants.filter((v) => v && Number.isFinite(Number(v.unit_price)))
      : [];
    if (variants.length > 1) {
      setProductWeightIdx(0);
      setProductWeightModal(product);
      return;
    }
    if (variants.length === 1) {
      const v = variants[0];
      const price = Number(v.unit_price);
      const label = v.weight ? `${product.name} · ${v.weight}` : product.name;
      addLineToBill(setLines, label, price, 1, meta);
      setMsg({ type: 'ok', text: `Added ${label}` });
      scheduleScannerFocus();
      return;
    }
    const price = parseFloat(product.unit_price);
    if (!Number.isFinite(price)) return;
    const label = product.weight ? `${product.name} · ${product.weight}` : product.name;
    addLineToBill(setLines, label, price, 1, meta);
    setMsg({ type: 'ok', text: `Added ${label}` });
    scheduleScannerFocus();
  };

  const submitProductWeightToCart = (e) => {
    e?.preventDefault?.();
    if (!productWeightModal) return;
    if (productIsOutOfStock(productWeightModal)) {
      setMsg({ type: 'error', text: `“${productWeightModal.name}” is out of stock` });
      return;
    }
    const variants = Array.isArray(productWeightModal.variants) ? productWeightModal.variants : [];
    const v = variants[productWeightIdx] || variants[0];
    if (!v) return;
    const price = Number(v.unit_price);
    if (!Number.isFinite(price)) {
      setMsg({ type: 'error', text: 'Invalid price for this weight' });
      return;
    }
    const label = v.weight ? `${productWeightModal.name} · ${v.weight}` : productWeightModal.name;
    addLineToBill(setLines, label, price, 1, {
      productId: productWeightModal.id,
      categoryId: productWeightModal.category_id || activeCategoryId
    });
    setMsg({ type: 'ok', text: `Added ${label}` });
    setProductWeightModal(null);
    setProductWeightIdx(0);
    scheduleScannerFocus();
  };

  const openCakeWeightPicker = (flavour) => {
    if (!registerSessionActive) {
      setRegisterSessionActive(true);
    }
    const key = String(flavour.name || '')
      .trim()
      .toUpperCase();
    const priced = cakePricesByFlavour[key] || [];
    const defaultWeight = priced[0]?.weight || 1;
    setCakeWeight(defaultWeight);
    setCakePrice(priced[0]?.price != null ? String(priced[0].price) : '');
    setCakeWeightModal({ id: flavour.id, name: flavour.name });
  };

  const closeCakeWeightPicker = () => {
    setCakeWeightModal(null);
    setCakePrice('');
    setCakePriceLoading(false);
  };

  const submitCakeWeightToCart = (e) => {
    e?.preventDefault?.();
    if (!cakeWeightModal?.name) return;
    const price = parseFloat(cakePrice);
    if (!Number.isFinite(price) || price < 0) {
      setMsg({ type: 'error', text: 'Enter a valid cake price (or configure the flavour price matrix).' });
      return;
    }
    const weightLabel = Number(cakeWeight);
    const desc = `Cake · ${cakeWeightModal.name} · ${weightLabel} kg`;
    addLineToBill(setLines, desc, price, 1);
    setMsg({ type: 'ok', text: `Added ${desc}` });
    closeCakeWeightPicker();
    scheduleScannerFocus();
  };

  const handleScannerKeyDown = (e) => {
    if (!registerSessionActive) return;
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const code = scannerInput.trim();
    if (!code) return;
    const isShortcode = /^\d{3}$/.test(code);
    setLastScan({
      code,
      isShortcode,
      time: new Date().toLocaleTimeString()
    });
    if (isShortcode) {
      applyScannedCode(code);
    } else {
      setMsg({ type: 'error', text: `Scanned "${code}" is not a 3-digit shortcode` });
    }
    setScannerInput('');
    scheduleScannerFocus();
  };

  const openExpenseModal = () => {
    setExpenseForm(emptyExpenseForm());
    setExpenseModalOpen(true);
  };

  const closeExpenseModal = () => {
    setExpenseModalOpen(false);
    setExpenseForm(emptyExpenseForm());
  };

  const addExpenseReceipt = (file) => {
    if (!file) return;
    setExpenseForm((f) => {
      if ((f.receiptFiles || []).length >= 3) return f;
      return { ...f, receiptFiles: [...(f.receiptFiles || []), file] };
    });
  };

  const removeExpenseReceipt = (idx) => {
    setExpenseForm((f) => ({
      ...f,
      receiptFiles: (f.receiptFiles || []).filter((_, i) => i !== idx)
    }));
  };

  const submitExpense = async (e) => {
    e.preventDefault();
    const sid = parseInt(storeId, 10);
    if (!sid) return setMsg({ type: 'error', text: 'Choose a store first' });
    if (!expenseForm.employeeName.trim()) {
      return setMsg({ type: 'error', text: 'Employee name is required' });
    }
    if (!expenseForm.reason.trim()) {
      return setMsg({ type: 'error', text: 'Purpose is required' });
    }
    const amt = Number(expenseForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      return setMsg({ type: 'error', text: 'Enter a valid amount' });
    }
    if (!expenseForm.expenseDate) {
      return setMsg({ type: 'error', text: 'Choose the expense date' });
    }
    setExpenseSubmitting(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('storeId', String(sid));
      fd.append('employeeName', expenseForm.employeeName.trim());
      fd.append('reason', expenseForm.reason.trim());
      fd.append('expenseDate', expenseForm.expenseDate || new Date().toISOString().slice(0, 10));
      fd.append('amount', String(amt));
      fd.append('paymentMethod', expenseForm.paymentMethod);
      (expenseForm.receiptFiles || []).slice(0, 3).forEach((file) => {
        fd.append('receipts', file);
      });
      await api.post('/pos/expenses', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      closeExpenseModal();
      setMsg({ type: 'ok', text: 'Expense added.' });
      loadExpenses();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not add expense' });
    } finally {
      setExpenseSubmitting(false);
    }
  };

  const formatExpensePayment = (method) => {
    if (method === 'paid_by_store') return 'Paid by the store';
    if (method === 'paid_by_self') return 'Paid by self';
    return method || '—';
  };

  const expenseReceiptLinks = (row) => {
    let paths = [];
    if (row.receipt_paths) {
      try {
        const parsed = typeof row.receipt_paths === 'string' ? JSON.parse(row.receipt_paths) : row.receipt_paths;
        if (Array.isArray(parsed)) paths = parsed.map((p) => (typeof p === 'string' ? p : p?.path)).filter(Boolean);
      } catch {
        paths = [];
      }
    }
    if (!paths.length && row.bill_file_path) paths = [row.bill_file_path];
    return paths;
  };

  const submitMenuCategory = async (e) => {
    e.preventDefault();
    setMsg(null);
    const sid = parseInt(storeId, 10);
    if (!sid) {
      setMsg({ type: 'error', text: 'Choose a store' });
      return;
    }
    const name = menuNewCat.name.trim();
    if (!name) {
      setMsg({ type: 'error', text: 'Category name required' });
      return;
    }
    try {
      await api.post('/pos/catalog/categories', {
        storeId: sid,
        name,
        sale_mode: menuNewCat.sale_mode === 'select' ? 'select' : 'barcode'
      });
      setMenuNewCat({ name: '', sale_mode: 'barcode' });
      setMsg({ type: 'ok', text: 'Category created.' });
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not create category' });
    }
  };

  const updateMenuCategoryMode = async (catId, sale_mode) => {
    try {
      await api.patch(`/pos/catalog/categories/${catId}`, { sale_mode });
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Update failed' });
    }
  };

  const deleteMenuCategory = async (catId) => {
    if (!window.confirm('Delete this category and all its products?')) return;
    try {
      await api.delete(`/pos/catalog/categories/${catId}`);
      setMsg({ type: 'ok', text: 'Category removed.' });
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Delete failed' });
    }
  };

  const openMenuAddModal = () => {
    const firstId = catalogCategories[0]?.id;
    setMenuAddForm({
      categoryId: firstId ? String(firstId) : '',
      name: '',
      description: '',
      weight: '',
      unitPrice: '',
      shortcode: '',
      image_url: ''
    });
    setMenuAddModalOpen(true);
  };

  const closeMenuAddModal = () => {
    setMenuAddModalOpen(false);
  };

  const submitMenuProductFromModal = async (e) => {
    e.preventDefault();
    setMsg(null);
    const categoryId = parseInt(menuAddForm.categoryId, 10);
    const name = menuAddForm.name.trim();
    const unitPrice = Number(menuAddForm.unitPrice);
    const cat = catalogCategories.find((c) => c.id === categoryId);
    if (!categoryId || !cat) {
      setMsg({ type: 'error', text: 'Choose a category' });
      return;
    }
    if (!name) {
      setMsg({ type: 'error', text: 'Product name required' });
      return;
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      setMsg({ type: 'error', text: 'Valid price required' });
      return;
    }
    if (cat.sale_mode === 'barcode') {
      const sc = String(menuAddForm.shortcode || '').trim();
      if (!/^\d{3}$/.test(sc)) {
        setMsg({ type: 'error', text: 'Barcode categories need a 3-digit short code' });
        return;
      }
    }
    try {
      await api.post('/pos/catalog/products', {
        categoryId,
        name,
        description: menuAddForm.description.trim(),
        weight: menuAddForm.weight.trim(),
        image_url: menuAddForm.image_url.trim(),
        unitPrice,
        shortcode: cat.sale_mode === 'barcode' ? String(menuAddForm.shortcode).trim() : ''
      });
      setMsg({ type: 'ok', text: 'Product added.' });
      setMenuAddModalOpen(false);
      setMenuAddForm({
        categoryId: catalogCategories[0]?.id ? String(catalogCategories[0].id) : '',
        name: '',
        description: '',
        weight: '',
        unitPrice: '',
        shortcode: '',
        image_url: ''
      });
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not add product' });
    }
  };

  const deleteMenuProduct = async (productId) => {
    if (!window.confirm('Remove this product?')) return;
    try {
      await api.delete(`/pos/catalog/products/${productId}`);
      setMsg({ type: 'ok', text: 'Product removed.' });
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Delete failed' });
    }
  };

  const openMenuEditProduct = (p, cat) => {
    setMenuEditForm({
      id: String(p.id),
      categoryId: String(cat.id),
      name: p.name || '',
      description: p.description || '',
      weight: p.weight || '',
      image_url: p.image_url || '',
      unitPrice: String(p.unit_price ?? ''),
      shortcode: p.shortcode != null ? String(p.shortcode) : ''
    });
    setMenuEditModalOpen(true);
  };

  const closeMenuEditModal = () => setMenuEditModalOpen(false);

  const submitMenuProductEdit = async (e) => {
    e.preventDefault();
    setMsg(null);
    const pid = parseInt(menuEditForm.id, 10);
    const categoryId = parseInt(menuEditForm.categoryId, 10);
    const name = menuEditForm.name.trim();
    const unitPrice = Number(menuEditForm.unitPrice);
    const cat = catalogCategories.find((c) => c.id === categoryId);
    if (!pid || !categoryId || !cat) {
      setMsg({ type: 'error', text: 'Choose a category' });
      return;
    }
    if (!name) {
      setMsg({ type: 'error', text: 'Product name required' });
      return;
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      setMsg({ type: 'error', text: 'Valid price required' });
      return;
    }
    if (menuEditSelectedCat?.sale_mode === 'barcode') {
      const sc = String(menuEditForm.shortcode || '').trim();
      if (!/^\d{3}$/.test(sc)) {
        setMsg({ type: 'error', text: 'Barcode categories need a 3-digit short code' });
        return;
      }
    }
    try {
      await api.patch(`/pos/catalog/products/${pid}`, {
        categoryId,
        name,
        description: menuEditForm.description.trim(),
        weight: menuEditForm.weight.trim(),
        image_url: menuEditForm.image_url.trim(),
        unitPrice,
        shortcode: menuEditSelectedCat?.sale_mode === 'barcode' ? String(menuEditForm.shortcode).trim() : ''
      });
      setMsg({ type: 'ok', text: 'Product updated.' });
      setMenuEditModalOpen(false);
      loadCatalog();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not update product' });
    }
  };

  const openSaleDetail = async (sale) => {
    setSaleDetailOpen(true);
    setSaleDetailLoading(true);
    setSaleDetailSale(null);
    setSaleDetailLines([]);
    setSaleDetailPayments([]);
    try {
      const { data } = await api.get(`/pos/sales/${sale.id}`);
      setSaleDetailSale(data.sale);
      setSaleDetailLines(data.lines || []);
      setSaleDetailPayments(data.payments || sale.payments || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not load sale' });
      setSaleDetailOpen(false);
    } finally {
      setSaleDetailLoading(false);
    }
  };

  const printSaleBill = async (sale, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      const { data } = await api.get(`/pos/sales/${sale.id}`);
      setLastReceipt(data);
      setReceiptWaPhone('');
      setReceiptWaStatus(null);
      setReceiptModalOpen(true);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not load bill for print' });
    }
  };

  const closeSaleDetail = () => {
    setSaleDetailOpen(false);
    setSaleDetailSale(null);
    setSaleDetailLines([]);
    setSaleDetailPayments([]);
  };

  const cancelSaleFromModal = async () => {
    if (!saleDetailSale?.id || saleDetailSale.cancelled_at) return;
    if (!window.confirm('Cancel this sale? It will stay listed as cancelled.')) return;
    try {
      const { data } = await api.post(`/pos/sales/${saleDetailSale.id}/cancel`);
      if (data?.sale) {
        setSaleDetailSale(data.sale);
      } else {
        const r = await api.get(`/pos/sales/${saleDetailSale.id}`);
        setSaleDetailSale(r.data.sale);
      }
      setMsg({ type: 'ok', text: 'Sale cancelled.' });
      loadSales();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Could not cancel sale' });
    }
  };

  if (isOrderCreator) {
    return (
      <div className="pos-hub cc-page">
        <Navbar />
        <header className="cc-page-head">
          <h1 className="cc-page-title">CRM — leads</h1>
          <p className="cc-page-lead">Capture catering inquiries and follow-ups by outlet.</p>
        </header>
        {msg ? (
          <div className={`pos-msg ${msg.type}`}>{msg.text}</div>
        ) : null}
        <div className="cc-card">
          <h2 style={{ marginTop: 0 }}>New lead</h2>
          <form onSubmit={submitLead}>
            <div className="pos-form-row">
              <label>
                Store
                <select value={leadForm.storeId} onChange={(e) => setLeadForm((f) => ({ ...f, storeId: e.target.value }))}>
                  <option value="">(Any / HQ)</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Name
                <input value={leadForm.contactName} onChange={(e) => setLeadForm((f) => ({ ...f, contactName: e.target.value }))} required />
              </label>
              <label>
                Phone
                <input value={leadForm.phone} onChange={(e) => setLeadForm((f) => ({ ...f, phone: e.target.value }))} required />
              </label>
            </div>
            <div className="pos-form-row">
              <label>
                Email
                <input type="email" value={leadForm.email} onChange={(e) => setLeadForm((f) => ({ ...f, email: e.target.value }))} />
              </label>
              <label>
                Company
                <input value={leadForm.company} onChange={(e) => setLeadForm((f) => ({ ...f, company: e.target.value }))} />
              </label>
              <label>
                Source
                <select value={leadForm.source} onChange={(e) => setLeadForm((f) => ({ ...f, source: e.target.value }))}>
                  {SOURCES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Notes
              <textarea rows={3} style={{ width: '100%' }} value={leadForm.notes} onChange={(e) => setLeadForm((f) => ({ ...f, notes: e.target.value }))} />
            </label>
            <button type="submit" className="btn btn-primary">
              Save lead
            </button>
          </form>
        </div>
        <div className="cc-card">
          <h2 className="cc-card-title" style={{ textTransform: 'none', fontSize: '1rem', color: 'var(--cc-ink)' }}>
            Recent leads
          </h2>
          {loadingLeads ? <p>Loading…</p> : null}
          <div className="pos-table-wrap">
            <table className="pos-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Store</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td>{formatDateTimeDisplay(l.created_at)}</td>
                    <td>{l.contact_name}</td>
                    <td>{l.phone}</td>
                    <td>{l.store_name || '—'}</td>
                    <td>
                      <select value={l.status} onChange={(e) => updateLeadStatus(l.id, e.target.value)}>
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const tabTitle =
    tab === 'register'
      ? 'New sale'
      : tab === 'sales'
        ? 'Sales reports'
        : tab === 'menu'
          ? 'Store menu'
          : tab === 'cakes'
            ? 'Cake orders'
            : tab === 'expenses'
              ? 'Expenses'
            : tab === 'leads'
              ? 'Leads'
              : tab === 'stores'
                ? 'Stores'
                : 'POS';

  return (
    <div className="pos-hub pos-cashier-app">
      <aside className="pos-cashier-sidebar" aria-label="POS navigation">
        <div className="pos-sidebar-brand-wrap">
          <span className="pos-sidebar-brand">Cake Express</span>
          <span className="pos-sidebar-sub">Point of Sale</span>
        </div>
        <nav className="pos-sidebar-nav">
          <div className="pos-sidebar-section-label">Categories</div>
          {catalogLoading && !catalogCategories.length ? <p className="pos-sidebar-hint">Loading menu…</p> : null}
          {!catalogLoading && parseInt(storeId, 10) && !tapCategories.length ? (
            <p className="pos-sidebar-hint">No categories yet — Admin adds them in Admin → Store menus.</p>
          ) : null}
          {tapCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`pos-sidebar-link pos-sidebar-cat${tab === 'register' && activeCategoryId === c.id ? ' is-active' : ''}`}
              onClick={() => {
                setTab('register');
                setActiveCategoryId(c.id);
                startRegisterSession();
              }}
            >
              <span className="pos-sidebar-cat-name">{c.name}</span>
              <span className="pos-sidebar-cat-badge">Tap</span>
            </button>
          ))}

          <div className="pos-sidebar-section-label">Actions</div>
          <button
            type="button"
            className={`pos-sidebar-link${tab === 'register' && !activeCategoryId ? ' is-active' : ''}`}
            onClick={() => {
              setTab('register');
              setActiveCategoryId(null);
              setRegisterSessionActive(true);
              setTimeout(() => scannerRef.current?.focus({ preventScroll: true }), 120);
            }}
          >
            New sale
          </button>
          <button type="button" className={`pos-sidebar-link${tab === 'sales' ? ' is-active' : ''}`} onClick={() => setTab('sales')}>
            Sales reports
          </button>
          <button type="button" className="pos-sidebar-link" onClick={openOpenItemModal}>
            Open item
          </button>
          <button type="button" className={`pos-sidebar-link${tab === 'cakes' ? ' is-active' : ''}`} onClick={() => setTab('cakes')}>
            Cake orders
          </button>
          <button type="button" className={`pos-sidebar-link${tab === 'expenses' ? ' is-active' : ''}`} onClick={() => setTab('expenses')}>
            Add Expense
          </button>
          {isAdmin ? (
            <a className="pos-sidebar-link" href="/pos-menus">
              Manage menus
            </a>
          ) : null}
          {showCrmLeads ? (
            <button type="button" className={`pos-sidebar-link${tab === 'leads' ? ' is-active' : ''}`} onClick={() => setTab('leads')}>
              Leads
            </button>
          ) : null}
          {isAdmin ? (
            <button type="button" className={`pos-sidebar-link${tab === 'stores' ? ' is-active' : ''}`} onClick={() => setTab('stores')}>
              Stores
            </button>
          ) : null}
        </nav>
        <div className="pos-sidebar-footer">
          <Link to="/" className="pos-sidebar-footer-link">
            Dashboard
          </Link>
          <Link to="/pos/label-print-test" className="pos-sidebar-footer-link">
            Label print test
          </Link>
          {posKpi ? (
            <p className="pos-sidebar-kpi">
              Today: {posKpi.todayCount} · {formatInr(posKpi.todayRevenue)}
            </p>
          ) : null}
        </div>
      </aside>

      <div className="pos-cashier-main">
        <header className="pos-cashier-topbar">
          <div className="pos-topbar-title-wrap">
            {tab === 'register' && barcodeCategories.length > 0 ? (
              <div className="pos-barcode-category-strip" aria-label="Barcode categories for this outlet">
                <span className="pos-barcode-strip-label">Scan categories</span>
                <div className="pos-barcode-strip-chips">
                  {barcodeCategories.map((c) => (
                    <span key={c.id} className="pos-barcode-chip">
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {tab === 'register' && parseInt(storeId, 10) ? (
              <div className="pos-pickup-kpi-strip" aria-label="Store pickup summary">
                <div className="pos-pickup-kpi-card">
                  <span className="pos-pickup-kpi-label">Today&apos;s pickups</span>
                  <strong className="pos-pickup-kpi-value" title="Picked up / total pickups">
                    {pickupSummary.todayPickedUp} / {pickupSummary.todayPickupTotal}
                  </strong>
                  <span className="pos-pickup-kpi-hint">picked up / total</span>
                </div>
                <div className="pos-pickup-kpi-card">
                  <span className="pos-pickup-kpi-label">Tomorrow</span>
                  <strong className="pos-pickup-kpi-value">{pickupSummary.tomorrowTotal}</strong>
                </div>
                <div className="pos-pickup-kpi-card">
                  <span className="pos-pickup-kpi-label">Future</span>
                  <strong className="pos-pickup-kpi-value">{pickupSummary.futureTotal}</strong>
                </div>
              </div>
            ) : null}
            <h1 className="pos-topbar-title">{tabTitle}</h1>
            {tab === 'register' ? (
              <p className="pos-topbar-crumb">
                Tap <strong>New order</strong> to scan · then use tap categories on the left for items without barcodes ·
                choose outlet below
              </p>
            ) : null}
          </div>
          <div className="pos-topbar-user">
            {tab === 'register' && isStoreStaff ? (
              <button
                type="button"
                className="btn btn-primary pos-new-cake-btn"
                onClick={() => {
                  if (!parseInt(storeId, 10)) {
                    setMsg({ type: 'error', text: 'Choose a store first' });
                    return;
                  }
                  setCakeWizardOpen(true);
                }}
              >
                New cake
              </button>
            ) : null}
            {posTopbarStoreLabel ? (
              <span className="pos-topbar-store" title="Outlet">
                {posTopbarStoreLabel}
              </span>
            ) : null}
            {isStoreStaff && parseInt(storeId, 10) ? (
              <button
                type="button"
                className="btn btn-secondary pos-day-close-btn"
                onClick={async () => {
                  setDayCloseOpen(true);
                  setDayCloseLoading(true);
                  setDayCloseData(null);
                  try {
                    const { data } = await api.get('/pos/day-close', {
                      params: { storeId: parseInt(storeId, 10) }
                    });
                    setDayCloseData(data);
                  } catch (e) {
                    setMsg({ type: 'error', text: e.response?.data?.error || 'Could not load day close' });
                    setDayCloseOpen(false);
                  } finally {
                    setDayCloseLoading(false);
                  }
                }}
              >
                Day close
              </button>
            ) : null}
            <span className="pos-user-badge" title={user?.email || ''}>
              {displayName}
            </span>
            <button type="button" className="btn btn-secondary pos-logout-btn" onClick={handlePosLogout}>
              Log out
            </button>
          </div>
        </header>

        {adminLivePreview ? (
          <div className="pos-msg info pos-msg-banner" role="status">
            Admin live view — pick a store below to see that outlet&apos;s POS menu in real time. Sales you complete
            here are real.
          </div>
        ) : null}

        {msg ? <div className={`pos-msg ${msg.type} pos-msg-banner`}>{msg.text}</div> : null}

        <div className="pos-cashier-scroll">
          {tab === 'register' && isStoreStaff ? (
            <div className="pos-register-page">
              <div className="pos-register-center">
                <div className="pos-register-toolbar-row">
                  <label className="pos-toolbar-field">
                    Store
                    <select
                      value={storeId}
                      onChange={(e) => setStoreId(e.target.value)}
                      required
                      disabled={Boolean(posDevice?.store_id)}
                    >
                      <option value="">Select…</option>
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.code})
                        </option>
                      ))}
                    </select>
                  </label>
                  {registerSessionActive && activeCategory ? (
                    <label className="pos-toolbar-field pos-toolbar-search">
                      Search {cakeCategoryActive ? 'flavours' : 'products'}
                      <input
                        type="search"
                        placeholder={cakeCategoryActive ? 'Filter flavours…' : 'Filter by name…'}
                        value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}
                      />
                    </label>
                  ) : null}
                </div>

                {!registerSessionActive ? (
                  <div className="pos-new-order-gate">
                    {!parseInt(storeId, 10) ? (
                      <p className="pos-new-order-gate-msg">Select an outlet above to start.</p>
                    ) : (
                      <>
                        <button type="button" className="btn btn-primary pos-new-order-btn" onClick={startRegisterSession}>
                          New order
                        </button>
                        <p className="pos-new-order-hint">Tap to turn on the barcode scanner and build this order.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="pos-scanner-bar pos-scanner-bar--primary">
                      <input
                        ref={scannerRef}
                        className="pos-scanner-input pos-scanner-bar-input pos-scanner-input--solo"
                        placeholder="Scan barcode or type 3-digit code + Enter"
                        autoComplete="off"
                        spellCheck={false}
                        value={scannerInput}
                        onChange={(e) => setScannerInput(e.target.value)}
                        onKeyDown={handleScannerKeyDown}
                        onBlur={handleScannerBlur}
                        aria-label="Barcode or short code"
                      />
                      {lastScan ? (
                        <span className={`pos-scan-pill ${lastScan.isShortcode ? 'ok' : ''}`}>
                          Last: {lastScan.code}
                        </span>
                      ) : null}
                    </div>

                    <div className="pos-product-board">
                      {!parseInt(storeId, 10) ? (
                        <p className="pos-stage-empty">Choose a store to load products.</p>
                      ) : !catalogCategories.length ? (
                        <p className="pos-stage-empty">
                          No menu for this outlet. An Admin can add categories under{' '}
                          <strong>Admin → POS menus</strong>.
                        </p>
                      ) : !tapCategories.length ? (
                        <div className="pos-mixed-mode-hint">
                          <p className="pos-mixed-mode-title">Barcode sale only</p>
                          <p className="pos-mixed-mode-text">
                            This outlet has no tap categories. Use the scanner field above for all items (short codes).
                            Scan categories are listed at the top of the screen.
                          </p>
                        </div>
                      ) : !activeCategory ? (
                        <div className="pos-mixed-mode-hint">
                          <p className="pos-mixed-mode-title">Pick tiles or keep scanning</p>
                          <p className="pos-mixed-mode-text">
                            Choose a <strong>tap category</strong> on the left to show product tiles, or stay here and{' '}
                            <strong>scan barcodes</strong> — both work at the same time.
                          </p>
                        </div>
                      ) : cakeCategoryActive ? (
                        filteredCakeFlavours.length ? (
                          <div className="pos-product-grid pos-product-grid--wide">
                            {filteredCakeFlavours.map((flavour) => {
                              const key = String(flavour.name || '')
                                .trim()
                                .toUpperCase();
                              const priced = cakePricesByFlavour[key] || [];
                              const fromPrice = priced[0]?.price;
                              return (
                                <button
                                  key={flavour.id}
                                  type="button"
                                  className="pos-product-tile pos-product-tile--cake"
                                  onClick={() => openCakeWeightPicker(flavour)}
                                >
                                  <span className="pos-product-tile-name">{flavour.name}</span>
                                  <span className="pos-product-tile-price">
                                    {Number.isFinite(fromPrice) ? `from ${formatInr(fromPrice)}` : 'Select weight'}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="pos-stage-empty">No cake flavours found (or nothing matches search).</p>
                        )
                      ) : (filteredProducts || []).length ? (
                        <div className="pos-product-grid pos-product-grid--wide">
                          {filteredProducts.map((p) => {
                            const variants = Array.isArray(p.variants) ? p.variants : [];
                            const fromPrice =
                              variants.length > 0
                                ? Math.min(...variants.map((v) => Number(v.unit_price)).filter(Number.isFinite))
                                : parseFloat(p.display_price ?? p.unit_price);
                            const oos = productIsOutOfStock(p);
                            const track = productTracksStock(p);
                            return (
                              <button
                                key={p.id}
                                type="button"
                                className={`pos-product-tile${oos ? ' is-oos' : ''}`}
                                disabled={oos}
                                onClick={() => tapProduct(p)}
                              >
                                {p.image_url ? (
                                  <img
                                    className="pos-product-tile-image"
                                    src={getImageUrl(p.image_url)}
                                    alt=""
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : null}
                                <span className="pos-product-tile-name">{p.name}</span>
                                <span className="pos-product-tile-price">
                                  {variants.length > 1 && Number.isFinite(fromPrice)
                                    ? `from ${formatInr(fromPrice)}`
                                    : formatInr(Number.isFinite(fromPrice) ? fromPrice : p.unit_price)}
                                </span>
                                {track ? (
                                  <span className={`pos-product-tile-stock${oos ? ' is-oos' : ''}`}>
                                    {oos ? 'Out of stock' : `Stock ${productStockQty(p)}`}
                                  </span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="pos-stage-empty">No products in this tap category (or nothing matches search).</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              <form className="pos-cart-panel" onSubmit={submitSale}>
                <h2 className="pos-cart-heading">Cart</h2>
                <div className="pos-cart-lines">
                  {lines.map((line, i) => {
                    if (!isReadyLine(line)) return null;
                    const unit = Number(line.unitPrice);
                    const qty = Number(line.qty) || 1;
                    const lineTotal = Number.isFinite(unit) ? Math.round(qty * unit * 100) / 100 : 0;
                    return (
                      <div key={i} className="pos-cart-row">
                        <div className="pos-cart-row-main">
                          <span className="pos-cart-name">{line.description}</span>
                          <span className="pos-cart-line-total">{formatInr(lineTotal)}</span>
                        </div>
                        <div className="pos-cart-row-controls">
                          <span className="pos-cart-unit">{formatInr(unit)} each</span>
                          <div className="pos-qty-stepper">
                            <button
                              type="button"
                              aria-label="Decrease quantity"
                              disabled={!registerSessionActive}
                              onClick={() => bumpLineQty(i, -1)}
                            >
                              −
                            </button>
                            <span className="pos-qty-val">{qty}</span>
                            <button
                              type="button"
                              aria-label="Increase quantity"
                              disabled={!registerSessionActive}
                              onClick={() => bumpLineQty(i, 1)}
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            className="pos-cart-remove"
                            disabled={!registerSessionActive}
                            onClick={() => removeLine(i)}
                            aria-label="Remove item"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!lines.some((l) => isReadyLine(l)) ? (
                    <p className="pos-cart-empty">
                      {!registerSessionActive
                        ? 'Tap New order in the center to activate the scanner.'
                        : 'Cart is empty — scan a barcode, tap a product, or use Open item.'}
                    </p>
                  ) : null}
                </div>

                <div className="pos-cart-discount-block">
                  <label className="pos-cart-discount-label" htmlFor="pos-cart-discount">
                    Discount (₹)
                  </label>
                  <input
                    id="pos-cart-discount"
                    type="text"
                    inputMode="numeric"
                    className="pos-cart-discount-input"
                    placeholder="0"
                    maxLength={8}
                    disabled={!registerSessionActive}
                    value={cartDiscount}
                    onChange={(e) => setCartDiscount(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    aria-describedby="pos-cart-discount-hint"
                  />
                  <p id="pos-cart-discount-hint" className="pos-cart-discount-hint">
                    Whole rupees off the cart. Max {formatInr(cartGross)} (current subtotal).
                    {cartDiscountRupees > 0 ? (
                      <>
                        {' '}
                        Applied: −{formatInr(cartDiscountRupees)}
                      </>
                    ) : null}
                  </p>
                </div>

                <label className="pos-split-toggle">
                  <input
                    type="checkbox"
                    checked={splitPayment}
                    disabled={!registerSessionActive}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setSplitPayment(on);
                      if (on) {
                        setSplitAmounts({
                          Cash: paymentMethod === 'Cash' ? String(cartTotal || '') : '',
                          Card: paymentMethod === 'Card' ? String(cartTotal || '') : '',
                          UPI: paymentMethod === 'UPI' ? String(cartTotal || '') : '',
                          Other: paymentMethod === 'Other' ? String(cartTotal || '') : ''
                        });
                        setSplitOtherSubtype(paymentOtherSubtype);
                      }
                    }}
                  />
                  Split payment (use more than one method)
                </label>

                {!splitPayment ? (
                  <>
                    <div className="pos-payment-row" role="group" aria-label="Payment type">
                      {PAYMENT.map((p) => (
                        <button
                          key={p}
                          type="button"
                          disabled={!registerSessionActive}
                          className={`pos-pay-pill${paymentMethod === p ? ' is-active' : ''}`}
                          onClick={() => setPaymentMethod(p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>

                    {paymentMethod === 'Other' ? (
                      <label className="pos-payment-other-field">
                        <span className="pos-payment-other-label">Other — specify</span>
                        <select
                          value={paymentOtherSubtype}
                          onChange={(e) => setPaymentOtherSubtype(e.target.value)}
                          disabled={!registerSessionActive}
                          className="pos-payment-other-select"
                        >
                          {PAYMENT_OTHER_OPTIONS.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </>
                ) : (
                  <div className="pos-split-panel" aria-label="Split payment amounts">
                    <p className="pos-split-hint">
                      Enter how much was paid by each method. Total must equal {formatInr(cartTotal)}.
                    </p>
                    {PAYMENT.map((method) => (
                      <div key={method} className="pos-split-row">
                        <label className="pos-split-method" htmlFor={`pos-split-${method}`}>
                          {method}
                        </label>
                        <input
                          id={`pos-split-${method}`}
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          disabled={!registerSessionActive}
                          className="pos-split-amount"
                          placeholder="0"
                          value={splitAmounts[method]}
                          onChange={(e) =>
                            setSplitAmounts((prev) => ({
                              ...prev,
                              [method]: e.target.value
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={!registerSessionActive || splitRemaining <= 0}
                          onClick={() => fillSplitRemaining(method)}
                        >
                          Fill rest
                        </button>
                      </div>
                    ))}
                    {(Number(splitAmounts.Other) || 0) > 0 ? (
                      <label className="pos-payment-other-field">
                        <span className="pos-payment-other-label">Other — specify</span>
                        <select
                          value={splitOtherSubtype}
                          onChange={(e) => setSplitOtherSubtype(e.target.value)}
                          disabled={!registerSessionActive}
                          className="pos-payment-other-select"
                        >
                          {PAYMENT_OTHER_OPTIONS.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    <div className="pos-split-summary">
                      <span>Split total: {formatInr(splitPaidTotal)}</span>
                      <span className={Math.abs(splitRemaining) < 0.01 ? 'is-ok' : 'is-warn'}>
                        {Math.abs(splitRemaining) < 0.01
                          ? 'Balanced'
                          : splitRemaining > 0
                            ? `Remaining ${formatInr(splitRemaining)}`
                            : `Over by ${formatInr(Math.abs(splitRemaining))}`}
                      </span>
                    </div>
                  </div>
                )}

                <div className="pos-cart-subtotal-row">
                  <span>Items subtotal</span>
                  <span>{formatInr(cartGross)}</span>
                </div>
                {cartDiscountRupees > 0 ? (
                  <div className="pos-cart-subtotal-row pos-cart-subtotal-row--discount">
                    <span>Discount</span>
                    <span>−{formatInr(cartDiscountRupees)}</span>
                  </div>
                ) : null}
                <div className="pos-cart-total-row">
                  <span className="pos-cart-total-label">Total to pay</span>
                  <span className="pos-cart-total-value">{formatInr(cartTotal)}</span>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary pos-complete-sale-btn"
                  disabled={
                    !registerSessionActive ||
                    (splitPayment && Math.abs(splitRemaining) > 0.009)
                  }
                >
                  Complete sale
                </button>
              </form>
            </div>
          ) : null}

      {tab === 'cakes' && isStoreStaff ? (
        <PosCakeOrdersTab
          storeId={storeId}
          formatInr={formatInr}
          serverDate={posKpi?.serverDate}
          orderCategory={orderCategoryForPos}
          onNewCakeOrder={() => {
            if (!parseInt(storeId, 10)) {
              setMsg({ type: 'error', text: 'Choose a store first' });
              return;
            }
            setCakeWizardOpen(true);
          }}
        />
      ) : null}

      {tab === 'menu' && isAdmin ? (
        <>
          <div className="cc-card pos-store-menu-shell pos-store-menu-shell--lux">
            <header className="pos-store-menu-head">
              <div className="pos-store-menu-head-text">
                <h2 className="pos-store-menu-title">Store menu</h2>
                <p className="pos-store-menu-lead">
                  Categories as tiles — products listed inside each. Barcode = short code; Tap = cashier tiles.
                </p>
              </div>
              <button
                type="button"
                className="btn pos-store-menu-add-btn pos-store-menu-add-btn--gold"
                onClick={openMenuAddModal}
                disabled={!parseInt(storeId, 10) || !catalogCategories.length}
                title={!parseInt(storeId, 10) ? 'Select a store first' : !catalogCategories.length ? 'Add a category below first' : ''}
              >
                + Add product
              </button>
            </header>

            <div className="pos-store-menu-toolbar">
              <label className="pos-store-menu-field">
                Outlet
                <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="pos-store-menu-select--lux">
                  <option value="">Select…</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <form className="pos-menu-new-cat pos-menu-new-cat--lux" onSubmit={submitMenuCategory}>
              <h3 className="pos-menu-section-title pos-menu-section-title--lux">New category</h3>
              <div className="pos-form-row">
                <label>
                  Name
                  <input
                    value={menuNewCat.name}
                    onChange={(e) => setMenuNewCat((m) => ({ ...m, name: e.target.value }))}
                    placeholder="e.g. Cakes, Beverages"
                  />
                </label>
                <label>
                  Sale mode
                  <select value={menuNewCat.sale_mode} onChange={(e) => setMenuNewCat((m) => ({ ...m, sale_mode: e.target.value }))}>
                    <option value="barcode">Barcode / short code</option>
                    <option value="select">Item select (tiles)</option>
                  </select>
                </label>
                <button type="submit" className="btn pos-btn-outline-gold" style={{ alignSelf: 'flex-end' }}>
                  Add category
                </button>
              </div>
            </form>

            {catalogLoading ? <p className="pos-store-menu-loading">Loading catalog…</p> : null}

            <div className="pos-admin-catalog-tiles">
              {catalogCategories.map((cat) => (
                <section key={cat.id} className="pos-admin-cat-tile">
                  <div className="pos-admin-cat-tile-inner">
                    <h3 className="pos-admin-cat-tile-title">{cat.name}</h3>
                    <span className={`pos-admin-cat-tile-badge${cat.sale_mode === 'barcode' ? ' is-barcode' : ''}`}>
                      {cat.sale_mode === 'barcode' ? 'Barcode' : 'Tap'}
                    </span>
                    <div className="pos-admin-cat-tile-controls">
                      <label className="pos-admin-cat-tile-mode">
                        <span className="visually-hidden">Sale mode</span>
                        <select
                          value={cat.sale_mode}
                          onChange={(e) => updateMenuCategoryMode(cat.id, e.target.value === 'select' ? 'select' : 'barcode')}
                          aria-label={`Mode for ${cat.name}`}
                        >
                          <option value="barcode">Barcode</option>
                          <option value="select">Tap</option>
                        </select>
                      </label>
                      <button type="button" className="pos-admin-cat-del" onClick={() => deleteMenuCategory(cat.id)}>
                        Delete
                      </button>
                    </div>
                    <ul className="pos-admin-product-stack">
                      {(cat.products || []).length ? (
                        (cat.products || []).map((p) => (
                          <li key={p.id} className="pos-admin-product-line">
                            {p.image_url ? (
                              <img className="pos-admin-product-image" src={p.image_url} alt="" loading="lazy" />
                            ) : null}
                            <div className="pos-admin-product-line-body">
                              <span className="pos-admin-product-line-name">{p.name}</span>
                              {p.description ? (
                                <span className="pos-admin-product-line-desc">{p.description}</span>
                              ) : null}
                              <span className="pos-admin-product-line-detail">
                                {cat.sale_mode === 'barcode' && p.shortcode ? (
                                  <span className="pos-admin-line-code">{p.shortcode}</span>
                                ) : null}
                                <span className="pos-admin-line-price">{formatInr(p.unit_price)}</span>
                                {p.weight ? <span className="pos-admin-line-weight">{p.weight}</span> : null}
                              </span>
                            </div>
                            <div className="pos-admin-product-line-actions">
                              <button
                                type="button"
                                className="pos-admin-line-edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMenuEditProduct(p, cat);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="pos-admin-line-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteMenuProduct(p.id);
                                }}
                                aria-label={`Remove ${p.name}`}
                              >
                                ×
                              </button>
                            </div>
                          </li>
                        ))
                      ) : (
                        <li className="pos-admin-product-line pos-admin-product-line--empty">
                          <span>No products — use Add product</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </section>
              ))}
            </div>
          </div>

          {menuAddModalOpen ? (
            <div className="pos-menu-modal-overlay pos-menu-modal-overlay--lux" role="presentation" onClick={closeMenuAddModal}>
              <div
                className="pos-menu-modal pos-menu-modal--lux"
                role="dialog"
                aria-labelledby="pos-add-product-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="pos-add-product-title" className="pos-menu-modal-title">
                  Add product
                </h3>
                <p className="pos-menu-modal-sub">Choose category, then enter details. Cake-specific options can be added later.</p>
                <form onSubmit={submitMenuProductFromModal} className="pos-menu-modal-form">
                  <label className="pos-menu-modal-field">
                    Category
                    <select
                      value={menuAddForm.categoryId}
                      onChange={(e) => setMenuAddForm((f) => ({ ...f, categoryId: e.target.value }))}
                      required
                    >
                      <option value="">Select category…</option>
                      {catalogCategories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.sale_mode === 'barcode' ? 'Barcode' : 'Tap'})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="pos-menu-modal-field">
                    Product name
                    <input
                      value={menuAddForm.name}
                      onChange={(e) => setMenuAddForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Display name on POS"
                      required
                    />
                  </label>
                  <label className="pos-menu-modal-field">
                    Description
                    <textarea
                      rows={3}
                      value={menuAddForm.description}
                      onChange={(e) => setMenuAddForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Short description for staff"
                    />
                  </label>
                  <label className="pos-menu-modal-field">
                    Product image URL
                    <input
                      type="text"
                      value={menuAddForm.image_url}
                      onChange={(e) => setMenuAddForm((f) => ({ ...f, image_url: e.target.value }))}
                      placeholder="https://… or /pos-images/item.jpg"
                    />
                  </label>
                  <div className="pos-menu-modal-row">
                    <label className="pos-menu-modal-field">
                      Price (₹)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={menuAddForm.unitPrice}
                        onChange={(e) => setMenuAddForm((f) => ({ ...f, unitPrice: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="pos-menu-modal-field">
                      Weight
                      <input
                        value={menuAddForm.weight}
                        onChange={(e) => setMenuAddForm((f) => ({ ...f, weight: e.target.value }))}
                        placeholder="e.g. 500 g, 1 kg"
                      />
                    </label>
                  </div>
                  {menuAddSelectedCat?.sale_mode === 'barcode' ? (
                    <label className="pos-menu-modal-field">
                      Short code (3 digits)
                      <input
                        value={menuAddForm.shortcode}
                        onChange={(e) => setMenuAddForm((f) => ({ ...f, shortcode: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
                        placeholder="001"
                        maxLength={3}
                        required
                      />
                    </label>
                  ) : null}
                  <div className="pos-menu-modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeMenuAddModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save product
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}

          {menuEditModalOpen ? (
            <div className="pos-menu-modal-overlay pos-menu-modal-overlay--lux" role="presentation" onClick={closeMenuEditModal}>
              <div
                className="pos-menu-modal pos-menu-modal--lux"
                role="dialog"
                aria-labelledby="pos-edit-product-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="pos-edit-product-title" className="pos-menu-modal-title">
                  Edit product
                </h3>
                <p className="pos-menu-modal-sub">Update details or move to another category for this store.</p>
                <form onSubmit={submitMenuProductEdit} className="pos-menu-modal-form">
                  <label className="pos-menu-modal-field">
                    Category
                    <select
                      value={menuEditForm.categoryId}
                      onChange={(e) => setMenuEditForm((f) => ({ ...f, categoryId: e.target.value }))}
                      required
                    >
                      {catalogCategories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.sale_mode === 'barcode' ? 'Barcode' : 'Tap'})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="pos-menu-modal-field">
                    Product name
                    <input
                      value={menuEditForm.name}
                      onChange={(e) => setMenuEditForm((f) => ({ ...f, name: e.target.value }))}
                      required
                    />
                  </label>
                  <label className="pos-menu-modal-field">
                    Description
                    <textarea
                      rows={3}
                      value={menuEditForm.description}
                      onChange={(e) => setMenuEditForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </label>
                  <label className="pos-menu-modal-field">
                    Product image URL
                    <input
                      type="text"
                      value={menuEditForm.image_url}
                      onChange={(e) => setMenuEditForm((f) => ({ ...f, image_url: e.target.value }))}
                      placeholder="https://… or /pos-images/item.jpg"
                    />
                  </label>
                  <div className="pos-menu-modal-row">
                    <label className="pos-menu-modal-field">
                      Price (₹)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={menuEditForm.unitPrice}
                        onChange={(e) => setMenuEditForm((f) => ({ ...f, unitPrice: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="pos-menu-modal-field">
                      Weight
                      <input
                        value={menuEditForm.weight}
                        onChange={(e) => setMenuEditForm((f) => ({ ...f, weight: e.target.value }))}
                        placeholder="e.g. 500 g"
                      />
                    </label>
                  </div>
                  {menuEditSelectedCat?.sale_mode === 'barcode' ? (
                    <label className="pos-menu-modal-field">
                      Short code (3 digits)
                      <input
                        value={menuEditForm.shortcode}
                        onChange={(e) =>
                          setMenuEditForm((f) => ({ ...f, shortcode: e.target.value.replace(/\D/g, '').slice(0, 3) }))
                        }
                        maxLength={3}
                        required
                      />
                    </label>
                  ) : null}
                  <div className="pos-menu-modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeMenuEditModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === 'sales' && isStoreStaff ? (
        <div className="cc-card pos-sales-reports-card">
          <div className="pos-sales-reports-head">
            <div>
              <h2 className="pos-sales-reports-title">Sales &amp; collections</h2>
              <p className="pos-sales-reports-hint">
                Counter sales, delivered cake collections, and open cake advances for the selected range.
              </p>
            </div>
            <div className="pos-sales-filters">
              <label className="pos-sales-date-field">
                From
                <input
                  type="date"
                  value={salesFrom}
                  onChange={(e) => setSalesFrom(e.target.value || todayIsoLocal())}
                />
              </label>
              <label className="pos-sales-date-field">
                To
                <input
                  type="date"
                  value={salesTo}
                  min={salesFrom}
                  onChange={(e) => setSalesTo(e.target.value || salesFrom)}
                />
              </label>
              <label className="pos-sales-date-field">
                Payment
                <select
                  value={salesPaymentFilter}
                  onChange={(e) => setSalesPaymentFilter(e.target.value)}
                >
                  <option value="all">All modes</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI / Online / QR</option>
                  <option value="others">Others</option>
                </select>
              </label>
              <label className="pos-sales-date-field">
                Product
                <select
                  value={salesProductFilter}
                  onChange={(e) => setSalesProductFilter(e.target.value)}
                >
                  <option value="all">All items</option>
                  <option value="cakes">Cakes</option>
                  <option value="beverages">Beverages</option>
                </select>
              </label>
            </div>
          </div>

          <div className="pos-sales-overview">
            <section className="pos-collect" aria-label="Collections summary">
              <div className="pos-collect__hero">
                <div className="pos-collect__hero-main">
                  <p className="pos-collect__eyebrow">Total collection</p>
                  <p className="pos-collect__amount">{formatInr(salesSummary.total)}</p>
                  <p className="pos-collect__period">
                    {salesFrom === salesTo
                      ? formatDateDisplay(salesFrom)
                      : `${formatDateDisplay(salesFrom)} → ${formatDateDisplay(salesTo)}`}
                  </p>
                </div>
                <div className="pos-collect__stats">
                  <button
                    type="button"
                    className={`pos-collect__stat${salesListFilter === 'completed' ? ' is-active' : ''}`}
                    onClick={() => setSalesListFilter((f) => (f === 'completed' ? 'all' : 'completed'))}
                  >
                    <em>Sales</em>
                    <strong>{salesSummary.saleCount || 0}</strong>
                    <span>counter tickets</span>
                  </button>
                  <div className="pos-collect__stat">
                    <em>Cake sales</em>
                    <strong>{salesSummary.cakeSaleCount || 0}</strong>
                    <span>delivered / picked up</span>
                  </div>
                  <div className="pos-collect__stat">
                    <em>Cake advances</em>
                    <strong>{salesSummary.cakeAdvanceCount || 0}</strong>
                    <span>awaiting pickup / delivery</span>
                  </div>
                  <button
                    type="button"
                    className={`pos-collect__stat${salesListFilter === 'all' ? ' is-active' : ''}`}
                    onClick={() => setSalesListFilter('all')}
                  >
                    <em>All entries</em>
                    <strong>{salesSummary.ticketCount || 0}</strong>
                    <span>in this range</span>
                  </button>
                  <button
                    type="button"
                    className={`pos-collect__stat pos-collect__stat--warn${
                      salesListFilter === 'cancelled' ? ' is-active' : ''
                    }`}
                    onClick={showCancelledSalesList}
                    disabled={!salesSummary.cancelledCount}
                    title={
                      salesSummary.cancelledCount
                        ? 'Show cancelled sales only'
                        : 'No cancelled sales in this range'
                    }
                  >
                    <em>Cancelled</em>
                    <strong>{salesSummary.cancelledCount || 0}</strong>
                    <span>{formatInr(salesSummary.cancelled)}</span>
                  </button>
                </div>
              </div>

              <div className="pos-collect__body">
                <div className="pos-collect__pay">
                  <div className="pos-collect__section-label">Payment mix</div>
                  <div className="pos-collect__pay-grid">
                    {[
                      { key: 'cash', label: 'Cash', value: salesSummary.cash },
                      { key: 'card', label: 'Card', value: salesSummary.card },
                      { key: 'upi', label: 'UPI', value: salesSummary.upi },
                      ...(salesSummary.others > 0
                        ? [{ key: 'others', label: 'Others', value: salesSummary.others }]
                        : [])
                    ].map((row) => {
                      const pct =
                        salesSummary.total > 0
                          ? Math.round((Number(row.value) / salesSummary.total) * 1000) / 10
                          : 0;
                      return (
                        <div key={row.key} className={`pos-collect__pay-row pos-collect__pay-row--${row.key}`}>
                          <div className="pos-collect__pay-top">
                            <span>{row.label}</span>
                            <strong>{formatInr(row.value)}</strong>
                          </div>
                          <div className="pos-collect__bar" aria-hidden>
                            <i style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                          <small>{pct}% of collection</small>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pos-collect__sources">
                  <div className="pos-collect__section-label">Source split</div>
                  <div className="pos-collect__source-card">
                    <span>Counter POS</span>
                    <strong>{formatInr(salesSummary.posTotal)}</strong>
                    <em>{salesSummary.saleCount || 0} sales</em>
                  </div>
                  <div className="pos-collect__source-card">
                    <span>Cake sales</span>
                    <strong>{formatInr(salesSummary.cakeSaleTotal || 0)}</strong>
                    <em>{salesSummary.cakeSaleCount || 0} delivered / picked up</em>
                  </div>
                  <div className="pos-collect__source-card">
                    <span>Cake advances</span>
                    <strong>{formatInr(salesSummary.cakeAdvanceTotal || 0)}</strong>
                    <em>{salesSummary.cakeAdvanceCount || 0} still open</em>
                  </div>
                </div>
              </div>
            </section>

            <div className="pos-sales-leaderboard" aria-label="Highest sold items">
              <div className="pos-sales-leaderboard__head">
                <h3>Top sellers</h3>
                <p>Highest quantity in this range</p>
              </div>
              {salesTopItems.length ? (
                <ol className="pos-sales-leaderboard__list">
                  {salesTopItems.slice(0, 8).map((item, idx) => (
                    <li key={`${item.name}-${idx}`}>
                      <span className="pos-sales-leaderboard__rank">{idx + 1}</span>
                      <div className="pos-sales-leaderboard__copy">
                        <strong>{item.name}</strong>
                        <small>
                          {item.category || item.kind}
                          {item.kind === 'cakes' || item.kind === 'beverages'
                            ? ` · ${item.kind}`
                            : ''}
                        </small>
                      </div>
                      <div className="pos-sales-leaderboard__nums">
                        <strong>{item.qty}</strong>
                        <small>{formatInr(item.revenue)}</small>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="pos-sales-reports-hint">No item sales in this range yet.</p>
              )}
            </div>
          </div>

          {loadingSales ? <p>Loading…</p> : null}

          {showCancelledOnly ? (
            <div className="pos-sales-filter-banner" role="status">
              <div>
                <strong>Cancelled sales only</strong>
                <span>
                  Showing {visibleSales.length} cancelled ticket
                  {visibleSales.length === 1 ? '' : 's'} for this range.
                </span>
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSalesListFilter('all')}>
                Show all sales
              </button>
            </div>
          ) : null}

          {!loadingSales && !sales.length && !cakeCollections.length ? (
            <p className="pos-sales-reports-hint">
              No sales or cake collections from {formatDateDisplay(salesFrom)}
              {salesTo !== salesFrom ? ` to ${formatDateDisplay(salesTo)}` : ''}.
            </p>
          ) : null}

          {cakeSaleRows.length && !showCancelledOnly ? (
            <div className="pos-sales-cake-block">
              <h3 className="pos-sales-subheading">POS cake sales (delivered / picked up)</h3>
              <div className="pos-table-wrap">
                <table className="pos-table pos-table--sales">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Mode</th>
                      <th>Type</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cakeSaleRows.map((c) => (
                      <tr key={`cake-sale-${c.id}`}>
                        <td>{formatDateDisplay(c.paymentDate)}</td>
                        <td>{c.orderNumber}</td>
                        <td>{c.customerName || '—'}</td>
                        <td>{c.paymentMode}</td>
                        <td>{c.posCakeMode || 'cake'}</td>
                        <td>₹{Number(c.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {cakeAdvanceRows.length && !showCancelledOnly ? (
            <div className="pos-sales-cake-block">
              <h3 className="pos-sales-subheading">POS cake advances (awaiting pickup / delivery)</h3>
              <div className="pos-table-wrap">
                <table className="pos-table pos-table--sales">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Mode</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cakeAdvanceRows.map((c) => (
                      <tr key={`cake-adv-${c.id}`}>
                        <td>{formatDateDisplay(c.paymentDate)}</td>
                        <td>{c.orderNumber}</td>
                        <td>{c.customerName || '—'}</td>
                        <td>{c.paymentMode}</td>
                        <td>{c.posCakeMode || 'cake'}</td>
                        <td>{c.orderStatus || 'Pending'}</td>
                        <td>₹{Number(c.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <h3 className="pos-sales-subheading">
            {showCancelledOnly ? 'Cancelled counter sales' : 'Counter sales'}
          </h3>
          {!loadingSales && showCancelledOnly && !visibleSales.length ? (
            <p className="pos-sales-reports-hint">No cancelled sales in this range.</p>
          ) : null}
          <div className="pos-table-wrap">
            <table className="pos-table pos-table--sales">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Receipt</th>
                  <th>Store</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Subtotal</th>
                  <th className="pos-sale-col-print">Bill</th>
                </tr>
              </thead>
              <tbody>
                {visibleSales.map((s) => (
                  <tr
                    key={s.id}
                    className={`pos-sale-table-row${s.cancelled_at ? ' is-cancelled' : ''}`}
                    onClick={() => openSaleDetail(s)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openSaleDetail(s);
                      }
                    }}
                  >
                    <td>{formatDateTimeDisplay(s.created_at)}</td>
                    <td>{s.receipt_no}</td>
                    <td>{s.store_name}</td>
                    <td>{formatPaymentDisplay(s, s.payments)}</td>
                    <td>{s.cancelled_at ? 'Cancelled' : 'Completed'}</td>
                    <td>₹{Number(s.subtotal).toFixed(2)}</td>
                    <td className="pos-sale-col-print">
                      <button
                        type="button"
                        className="pos-sale-print-btn"
                        title="Print bill"
                        aria-label={`Print bill ${s.receipt_no}`}
                        onClick={(e) => printSaleBill(s, e)}
                      >
                        <AppIcon name="print" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'expenses' && isStoreStaff ? (
        <div className="cc-card pos-expenses-card">
          <div className="pos-expenses-head">
            <div>
              <h2 className="pos-sales-reports-title">Expenses</h2>
              <p className="pos-sales-reports-hint">Filter by date range to see total spend for the period.</p>
            </div>
            <button type="button" className="btn btn-primary" onClick={openExpenseModal}>
              Add Expense
            </button>
          </div>
          <div className="pos-sales-filters pos-expenses-filters">
            <label className="pos-sales-date-field">
              From
              <input
                type="date"
                value={expensesFrom}
                onChange={(e) => setExpensesFrom(e.target.value || todayIsoLocal())}
              />
            </label>
            <label className="pos-sales-date-field">
              To
              <input
                type="date"
                value={expensesTo}
                min={expensesFrom}
                onChange={(e) => setExpensesTo(e.target.value || expensesFrom)}
              />
            </label>
            <button type="button" className="btn btn-secondary" onClick={loadExpenses} disabled={loadingExpenses}>
              {loadingExpenses ? 'Loading…' : 'Apply'}
            </button>
          </div>
          <div className="pos-expenses-summary" aria-label="Expense totals">
            <div className="pos-expenses-summary__card">
              <em>Total expenditure</em>
              <strong>{formatInr(expensesSummary.totalAmount || 0)}</strong>
              <span>
                {expensesSummary.count || 0} expense
                {(expensesSummary.count || 0) === 1 ? '' : 's'}
                {' · '}
                {expensesFrom === expensesTo
                  ? formatDateDisplay(expensesFrom)
                  : `${formatDateDisplay(expensesFrom)} → ${formatDateDisplay(expensesTo)}`}
              </span>
            </div>
            <div className="pos-expenses-summary__card">
              <em>Pending</em>
              <strong>{formatInr(expensesSummary.pendingAmount || 0)}</strong>
            </div>
            <div className="pos-expenses-summary__card">
              <em>Approved</em>
              <strong>{formatInr(expensesSummary.approvedAmount || 0)}</strong>
            </div>
          </div>
          {loadingExpenses ? <p>Loading…</p> : null}
          <div className="pos-table-wrap">
            <table className="pos-table pos-table--sales">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Purpose</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Store</th>
                  <th>Status</th>
                  <th>Receipts</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => {
                  const receipts = expenseReceiptLinks(e);
                  const st = e.status || 'pending';
                  return (
                    <tr key={e.id}>
                      <td>{formatDateDisplay(e.expense_date)}</td>
                      <td>{e.employee_name || '—'}</td>
                      <td>{e.reason}</td>
                      <td>{formatInr(e.amount)}</td>
                      <td>{formatExpensePayment(e.payment_method)}</td>
                      <td>{e.store_name}</td>
                      <td>
                        <span className={`exp-badge exp-badge--${st}`}>
                          {st}
                        </span>
                      </td>
                      <td>
                        {receipts.length ? (
                          <span className="pos-expense-receipts">
                            {receipts.map((href, i) => (
                              <a key={`${e.id}-r-${i}`} href={href} target="_blank" rel="noreferrer">
                                #{i + 1}
                              </a>
                            ))}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loadingExpenses && !expenses.length ? (
                  <tr>
                    <td colSpan={8}>
                      No expenses from {formatDateDisplay(expensesFrom)}
                      {expensesTo !== expensesFrom ? ` to ${formatDateDisplay(expensesTo)}` : ''}.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'leads' && showCrmLeads ? (
        <div className="cc-card">
          <h2 style={{ marginTop: 0, fontSize: '1.05rem', color: 'var(--cc-ink)' }}>New lead</h2>
          <form onSubmit={submitLead}>
            <div className="pos-form-row">
              <label>
                Store
                <select value={leadForm.storeId} onChange={(e) => setLeadForm((f) => ({ ...f, storeId: e.target.value }))}>
                  <option value="">(Any / HQ)</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Name
                <input value={leadForm.contactName} onChange={(e) => setLeadForm((f) => ({ ...f, contactName: e.target.value }))} required />
              </label>
              <label>
                Phone
                <input value={leadForm.phone} onChange={(e) => setLeadForm((f) => ({ ...f, phone: e.target.value }))} required />
              </label>
            </div>
            <div className="pos-form-row">
              <label>
                Source
                <select value={leadForm.source} onChange={(e) => setLeadForm((f) => ({ ...f, source: e.target.value }))}>
                  {SOURCES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Notes
                <input value={leadForm.notes} onChange={(e) => setLeadForm((f) => ({ ...f, notes: e.target.value }))} />
              </label>
            </div>
            <button type="submit" className="btn btn-primary">
              Save lead
            </button>
          </form>
          <h2>Pipeline</h2>
          {loadingLeads ? <p>Loading…</p> : null}
          <div className="pos-table-wrap">
            <table className="pos-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Store</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td>{formatDateTimeDisplay(l.created_at)}</td>
                    <td>{l.contact_name}</td>
                    <td>{l.phone}</td>
                    <td>{l.store_name || '—'}</td>
                    <td>
                      <select value={l.status} onChange={(e) => updateLeadStatus(l.id, e.target.value)}>
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'stores' && isAdmin ? (
        <div className="cc-card pos-inner-panel">
          <h2 style={{ marginTop: 0, fontSize: '1.05rem', color: 'var(--cc-ink)' }}>Add franchise / outlet</h2>
          <form onSubmit={createStore}>
            <div className="pos-form-row">
              <label>
                Code (unique)
                <input value={newStore.code} onChange={(e) => setNewStore((s) => ({ ...s, code: e.target.value }))} placeholder="HYD-BANJARA" required />
              </label>
              <label>
                Display name
                <input value={newStore.name} onChange={(e) => setNewStore((s) => ({ ...s, name: e.target.value }))} required />
              </label>
              <label>
                City
                <input value={newStore.city} onChange={(e) => setNewStore((s) => ({ ...s, city: e.target.value }))} />
              </label>
              <label>
                Phone
                <input value={newStore.phone} onChange={(e) => setNewStore((s) => ({ ...s, phone: e.target.value }))} />
              </label>
              <label>
                GSTIN (optional)
                <input
                  value={newStore.gstin}
                  onChange={(e) =>
                    setNewStore((s) => ({
                      ...s,
                      gstin: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15)
                    }))
                  }
                  minLength={15}
                  maxLength={15}
                />
              </label>
            </div>
            <label>
              Address
              <textarea
                rows={3}
                value={newStore.address}
                onChange={(e) => setNewStore((s) => ({ ...s, address: e.target.value }))}
                required
              />
            </label>
            <button type="submit" className="btn btn-primary">
              Create store
            </button>
          </form>
          <h3 className="cc-section-label" style={{ marginTop: '1.5rem' }}>
            Active stores
          </h3>
          <div className="cc-store-grid">
            {stores.map((s) => (
              <div key={s.id} className="cc-store-card">
                <h4>{s.name}</h4>
                <dl>
                  <div>
                    <dt>Code</dt>
                    <dd>
                      <code>{s.code}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>City</dt>
                    <dd>{s.city || '—'}</dd>
                  </div>
                  <div>
                    <dt>GSTIN</dt>
                    <dd>{s.gstin || 'Not added'}</dd>
                  </div>
                </dl>
                {s.address ? <p>{s.address}</p> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
        </div>

        {expenseModalOpen ? (
          <div className="pos-menu-modal-overlay pos-menu-modal-overlay--lux" onClick={closeExpenseModal}>
            <div
              className="pos-menu-modal pos-menu-modal--lux pos-expense-modal"
              role="dialog"
              aria-labelledby="pos-expense-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pos-expense-modal-head">
                <h3 id="pos-expense-title" className="pos-menu-modal-title">
                  Add Expense
                </h3>
                <button type="button" className="pos-sale-detail-close" onClick={closeExpenseModal} aria-label="Close">
                  ×
                </button>
              </div>
              <form className="pos-menu-modal-form pos-expense-form" onSubmit={submitExpense}>
                <label className="pos-menu-modal-field">
                  Employee name
                  <input
                    value={expenseForm.employeeName}
                    onChange={(e) => setExpenseForm((f) => ({ ...f, employeeName: e.target.value }))}
                    placeholder="Who is logging this expense?"
                    autoFocus
                    required
                  />
                </label>

                <label className="pos-menu-modal-field">
                  Purpose
                  <textarea
                    rows={2}
                    value={expenseForm.reason}
                    onChange={(e) => setExpenseForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="What was this expense for?"
                    required
                  />
                </label>

                <div className="pos-menu-modal-row">
                  <label className="pos-menu-modal-field">
                    Amount (₹)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, amount: e.target.value }))}
                      required
                    />
                  </label>
                  <label className="pos-menu-modal-field">
                    Date
                    <input
                      type="date"
                      value={expenseForm.expenseDate}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, expenseDate: e.target.value }))}
                      required
                    />
                  </label>
                </div>

                <label className="pos-menu-modal-field">
                  Payment info
                  <select
                    value={expenseForm.paymentMethod}
                    onChange={(e) => setExpenseForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="Other">Other</option>
                    <option value="paid_by_self">Paid by self</option>
                    <option value="paid_by_store">Paid by the store</option>
                  </select>
                </label>

                <div className="pos-expense-receipts-block">
                  <div className="pos-expense-receipts-block-head">
                    <span>Receipt images</span>
                    <small>Max 3 · optional</small>
                  </div>
                  <div className="pos-expense-receipt-slots">
                    {[0, 1, 2].map((slot) => {
                      const file = (expenseForm.receiptFiles || [])[slot];
                      return (
                        <div key={slot} className={`pos-expense-receipt-slot${file ? ' has-file' : ''}`}>
                          <span className="pos-expense-receipt-slot-label">Receipt {slot + 1}</span>
                          {file ? (
                            <div className="pos-expense-receipt-slot-file">
                              <span title={file.name}>{file.name}</span>
                              <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeExpenseReceipt(slot)}>
                                Remove
                              </button>
                            </div>
                          ) : (
                            <label className="pos-expense-receipt-slot-upload">
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                disabled={(expenseForm.receiptFiles || []).length !== slot}
                                onChange={(e) => {
                                  addExpenseReceipt(e.target.files?.[0] || null);
                                  e.target.value = '';
                                }}
                              />
                              {(expenseForm.receiptFiles || []).length === slot ? 'Choose image' : 'Add previous first'}
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pos-menu-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeExpenseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={expenseSubmitting}>
                    {expenseSubmitting ? 'Saving…' : 'Submit expense'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {saleDetailOpen ? (
          <div
            className="pos-sale-detail-overlay pos-menu-modal-overlay--lux"
            role="presentation"
            onClick={closeSaleDetail}
          >
            <div
              className="pos-sale-detail-modal pos-menu-modal--lux"
              role="dialog"
              aria-labelledby="pos-sale-detail-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pos-sale-detail-head">
                <h3 id="pos-sale-detail-title" className="pos-menu-modal-title">
                  Sale details
                </h3>
                <button type="button" className="pos-sale-detail-close" onClick={closeSaleDetail} aria-label="Close">
                  ×
                </button>
              </div>
              {saleDetailLoading ? (
                <p className="pos-sale-detail-loading">Loading…</p>
              ) : saleDetailSale ? (
                <>
                  <p className="pos-sale-detail-meta">
                    <strong>{saleDetailSale.receipt_no}</strong>
                    <span>
                      {saleDetailSale.store_name}
                      {' · '}
                      {formatDateTimeDisplay(saleDetailSale.created_at)}
                    </span>
                  </p>
                  <p className="pos-sale-detail-staff">
                    <span className="pos-sale-detail-staff-label">Recorded by</span>
                    <span className="pos-sale-detail-staff-value">
                      {saleDetailSale.created_by_name || '—'}
                    </span>
                  </p>
                  <p className="pos-sale-detail-pay">
                    Payment: <strong>{formatPaymentDisplay(saleDetailSale, saleDetailPayments)}</strong>
                    {saleDetailSale.notes ? (
                      <span className="pos-sale-detail-notes"> · Note: {saleDetailSale.notes}</span>
                    ) : null}
                  </p>
                  {saleDetailSale.cancelled_at ? (
                    <div className="pos-sale-detail-cancelled-banner" role="status">
                      <strong className="pos-sale-detail-cancelled-title">Cancelled sale</strong>
                      <div className="pos-sale-detail-cancel-meta">
                        {saleDetailSale.cancelled_by_name ? (
                          <>
                            Cancelled by <strong>{saleDetailSale.cancelled_by_name}</strong>
                          </>
                        ) : (
                          <>Cancelled by (not recorded — older cancellation)</>
                        )}
                        {' · '}
                        {formatDateTimeDisplay(saleDetailSale.cancelled_at)}
                      </div>
                    </div>
                  ) : null}
                  <table className="pos-sale-detail-lines">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Line</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(saleDetailLines || []).map((ln) => (
                        <tr key={ln.id}>
                          <td>{ln.description}</td>
                          <td>{Number(ln.qty)}</td>
                          <td>₹{Number(ln.unit_price).toFixed(2)}</td>
                          <td>₹{Number(ln.line_total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(() => {
                    const lineSum = (saleDetailLines || []).reduce(
                      (a, ln) => a + Number(ln.line_total || 0),
                      0
                    );
                    const disc = Number(saleDetailSale.discount_amount || 0);
                    return (
                      <>
                        <div className="pos-sale-detail-total">
                          <span>Items subtotal</span>
                          <strong>₹{lineSum.toFixed(2)}</strong>
                        </div>
                        {disc > 0 ? (
                          <div className="pos-sale-detail-total pos-sale-detail-total--discount">
                            <span>Discount</span>
                            <strong>−₹{disc.toFixed(2)}</strong>
                          </div>
                        ) : null}
                        <div className="pos-sale-detail-total pos-sale-detail-total--grand">
                          <span>Total charged</span>
                          <strong>₹{Number(saleDetailSale.subtotal).toFixed(2)}</strong>
                        </div>
                      </>
                    );
                  })()}
                  <div className="pos-sale-detail-actions">
                    {isStoreStaff && !saleDetailSale.cancelled_at ? (
                      <button type="button" className="btn pos-sale-cancel-btn" onClick={cancelSaleFromModal}>
                        Cancel sale
                      </button>
                    ) : null}
                    <button type="button" className="btn btn-primary" onClick={closeSaleDetail}>
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <p className="pos-sale-detail-loading">No data.</p>
              )}
            </div>
          </div>
        ) : null}

        <PosCakeWizard
          open={cakeWizardOpen}
          onClose={() => setCakeWizardOpen(false)}
          storeId={storeId}
          orderCategory={orderCategoryForPos}
          onAddCounterLine={(desc, price) => addLineToBill(setLines, desc, price)}
          formatInr={formatInr}
          registerSessionActive={registerSessionActive}
          serverDate={posKpi?.serverDate}
        />

        {receiptModalOpen && lastReceipt?.sale ? (
          <div
            className="pos-receipt-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pos-receipt-title"
            onClick={closeReceiptModal}
          >
            <div className="pos-receipt-modal" onClick={(e) => e.stopPropagation()}>
              <div id="pos-receipt-print-area" className="pos-receipt-slip">
                <div className="pos-receipt-brand">Cake Express</div>
                <h2 id="pos-receipt-title" className="pos-receipt-heading">
                  Receipt
                </h2>
                <p className="pos-receipt-meta">{lastReceipt.sale.store_name}</p>
                <p className="pos-receipt-meta">
                  {lastReceipt.sale.receipt_no} · {formatDateTimeDisplay(lastReceipt.sale.created_at)}
                </p>
                <table className="pos-receipt-table">
                  <tbody>
                    {(lastReceipt.lines || []).map((ln) => (
                      <tr key={ln.id}>
                        <td>
                          <div className="pos-receipt-item-name">{ln.description}</div>
                          <div className="pos-receipt-item-qty">
                            {Number(ln.qty)} × ₹{Number(ln.unit_price).toFixed(2)}
                          </div>
                        </td>
                        <td className="pos-receipt-item-amt">₹{Number(ln.line_total).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(() => {
                  const disc = Number(lastReceipt.sale.discount_amount || 0);
                  const gross = (lastReceipt.lines || []).reduce(
                    (a, ln) => a + Number(ln.line_total || 0),
                    0
                  );
                  return (
                    <>
                      <div className="pos-receipt-total-row">
                        <span>Items</span>
                        <span>₹{gross.toFixed(2)}</span>
                      </div>
                      {disc > 0 ? (
                        <div className="pos-receipt-total-row pos-receipt-total-row--discount">
                          <span>Discount</span>
                          <span>−₹{disc.toFixed(2)}</span>
                        </div>
                      ) : null}
                      <div className="pos-receipt-total-row pos-receipt-total-row--grand">
                        <span>Total</span>
                        <span>₹{Number(lastReceipt.sale.subtotal).toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
                <p className="pos-receipt-pay">
                  Payment: {formatPaymentDisplay(lastReceipt.sale, lastReceipt.payments)}
                </p>
                <p className="pos-receipt-session-hint no-print">
                  You stay signed in while working. After 5 minutes with no taps or keys, you will be signed out so the
                  next cashier can enter their PIN.
                </p>
                <p className="pos-receipt-thanks">Thank you!</p>
                <p className="pos-receipt-instagram">instagram.com/cakexpress_official</p>
              </div>

              <form className="pos-receipt-wa no-print" onSubmit={sendReceiptWhatsApp}>
                <label className="pos-receipt-wa-label" htmlFor="pos-receipt-wa-phone">
                  Send receipt on WhatsApp
                </label>
                <div className="pos-receipt-wa-row">
                  <input
                    id="pos-receipt-wa-phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="Customer mobile (10 digits)"
                    value={receiptWaPhone}
                    onChange={(e) => setReceiptWaPhone(e.target.value)}
                    disabled={receiptWaSending}
                  />
                  <button type="submit" className="btn btn-primary" disabled={receiptWaSending}>
                    {receiptWaSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {receiptWaStatus ? (
                  <p className={`pos-receipt-wa-status is-${receiptWaStatus.type}`}>{receiptWaStatus.text}</p>
                ) : (
                  <p className="pos-receipt-wa-hint">Enter the customer number, then tap Send.</p>
                )}
              </form>

              <div className="pos-receipt-actions no-print">
                <button type="button" className="btn btn-primary" onClick={printReceipt}>
                  Print
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeReceiptModal}>
                  Close
                </button>
              </div>
              <p className="pos-receipt-print-hint no-print">
                Print opens the browser print dialog — choose your <strong>Tenax</strong> thermal printer as the destination for kiosk receipts (set it as default in the OS for one-click printing).
              </p>
            </div>
          </div>
        ) : null}

        {openItemModalOpen ? (
          <div
            className="pos-menu-modal-overlay pos-open-item-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pos-open-item-title"
            onClick={() => setOpenItemModalOpen(false)}
          >
            <div className="pos-menu-modal pos-menu-modal--lux pos-open-item-modal" onClick={(e) => e.stopPropagation()}>
              <h3 id="pos-open-item-title" className="pos-menu-modal-title">
                Open item
              </h3>
              <p className="pos-menu-modal-sub">Add a custom item name, price, and quantity to the cart.</p>
              <form className="pos-menu-modal-form" onSubmit={submitOpenItem}>
                <label className="pos-menu-modal-field">
                  Item name
                  <input
                    autoFocus
                    value={openItemForm.name}
                    onChange={(e) => setOpenItemForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Custom pastry"
                    required
                  />
                </label>
                <div className="pos-menu-modal-row">
                  <label className="pos-menu-modal-field">
                    Price (₹)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={openItemForm.price}
                      onChange={(e) => setOpenItemForm((f) => ({ ...f, price: e.target.value }))}
                      placeholder="0"
                      required
                    />
                  </label>
                  <label className="pos-menu-modal-field">
                    Quantity
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={openItemForm.qty}
                      onChange={(e) => setOpenItemForm((f) => ({ ...f, qty: e.target.value }))}
                      required
                    />
                  </label>
                </div>
                <div className="pos-menu-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setOpenItemModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add to cart
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {productWeightModal ? (
          <div
            className="pos-menu-modal-overlay pos-cake-weight-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pos-product-weight-title"
            onClick={() => setProductWeightModal(null)}
          >
            <div
              className="pos-menu-modal pos-menu-modal--lux pos-cake-weight-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="pos-product-weight-title" className="pos-menu-modal-title">
                {productWeightModal.name}
              </h3>
              <p className="pos-menu-modal-sub">Choose weight, then add to cart.</p>
              <form className="pos-menu-modal-form" onSubmit={submitProductWeightToCart}>
                <div className="pos-cake-weight-grid" role="group" aria-label="Product weight">
                  {(productWeightModal.variants || []).map((v, idx) => (
                    <button
                      key={`${v.weight}-${idx}`}
                      type="button"
                      className={`pos-cake-weight-chip${productWeightIdx === idx ? ' is-active' : ''}`}
                      onClick={() => setProductWeightIdx(idx)}
                    >
                      {v.weight}
                      <span style={{ display: 'block', fontSize: 12, opacity: 0.85 }}>
                        {formatInr(v.unit_price)}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="pos-menu-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setProductWeightModal(null)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add to cart
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {cakeWeightModal ? (
          <div
            className="pos-menu-modal-overlay pos-cake-weight-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pos-cake-weight-title"
            onClick={closeCakeWeightPicker}
          >
            <div
              className="pos-menu-modal pos-menu-modal--lux pos-cake-weight-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="pos-cake-weight-title" className="pos-menu-modal-title">
                {cakeWeightModal.name}
              </h3>
              <p className="pos-menu-modal-sub">Choose cake weight, confirm price, then add to cart.</p>
              <form className="pos-menu-modal-form" onSubmit={submitCakeWeightToCart}>
                <label className="pos-menu-modal-field">
                  Weight (kg)
                  <select
                    value={cakeWeight}
                    onChange={(e) => setCakeWeight(parseFloat(e.target.value))}
                    required
                  >
                    {cakeWeightOptions.map((w) => (
                      <option key={w} value={w}>
                        {w} kg
                      </option>
                    ))}
                  </select>
                </label>
                <label className="pos-menu-modal-field">
                  Price (₹){cakePriceLoading ? ' · looking up…' : ''}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={cakePrice}
                    onChange={(e) => setCakePrice(e.target.value)}
                    placeholder="Auto from price matrix"
                    required
                  />
                </label>
                <div className="pos-menu-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeCakeWeightPicker}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={cakePriceLoading}>
                    Add to cart ({formatInr(parseFloat(cakePrice) || 0)})
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {dayCloseOpen ? (
          <div
            className="pos-menu-modal-overlay pos-menu-modal-overlay--lux"
            role="presentation"
            onClick={() => !dayCloseSending && setDayCloseOpen(false)}
          >
            <div
              className="pos-menu-modal pos-menu-modal--lux pos-day-close-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pos-day-close-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="pos-day-close-title" className="pos-menu-modal-title">
                Day close
              </h3>
              <p className="pos-menu-modal-sub pos-day-close-sub">
                {dayCloseData
                  ? `${dayCloseData.storeName || dayCloseData.storeCode || 'Store'} · ${formatDateDisplay(dayCloseData.date)}`
                  : 'Loading today’s summary…'}
              </p>
              {dayCloseLoading ? <p className="pos-day-close-loading">Loading summary…</p> : null}
              {dayCloseData ? (
                <div className="pos-day-close-body">
                  <div className="pos-day-close-hero">
                    <em>Total sales (counter + cakes)</em>
                    <strong>{formatInr(dayCloseData.summary?.total)}</strong>
                    <span>
                      {dayCloseData.summary?.ticketCount || dayCloseData.summary?.saleCount || 0} tickets
                      {' · '}
                      POS {formatInr(dayCloseData.summary?.posTotal)}
                      {' · '}
                      Cakes {formatInr(dayCloseData.summary?.cakeTotal)}
                    </span>
                  </div>
                  <div className="pos-day-close-grid">
                    <div>
                      <em>Cash</em>
                      <strong>{formatInr(dayCloseData.summary?.cash)}</strong>
                    </div>
                    <div>
                      <em>Card</em>
                      <strong>{formatInr(dayCloseData.summary?.card)}</strong>
                    </div>
                    <div>
                      <em>UPI</em>
                      <strong>{formatInr(dayCloseData.summary?.upi)}</strong>
                    </div>
                    <div>
                      <em>Other</em>
                      <strong>{formatInr(dayCloseData.summary?.others)}</strong>
                    </div>
                    <div>
                      <em>Cancelled</em>
                      <strong>
                        {dayCloseData.summary?.cancelledCount || 0}
                      </strong>
                      <span className="pos-day-close-mini">{formatInr(dayCloseData.summary?.cancelled)}</span>
                    </div>
                    <div>
                      <em>Cakes sold</em>
                      <strong>{dayCloseData.summary?.cakeSaleCount || 0}</strong>
                      <span className="pos-day-close-mini">{formatInr(dayCloseData.summary?.cakeSaleTotal)}</span>
                    </div>
                    <div className="pos-day-close-span2">
                      <em>Cake advances</em>
                      <strong>{formatInr(dayCloseData.summary?.cakeAdvanceTotal)}</strong>
                      <span className="pos-day-close-mini">
                        {dayCloseData.summary?.cakeAdvanceCount || 0} payments
                      </span>
                    </div>
                  </div>

                  <div className="pos-day-close-leftover-block">
                    <h4>Leftover veg snacks</h4>
                    <ul className="pos-day-close-leftovers">
                      {(dayCloseData.leftovers?.veg || []).length === 0 ? (
                        <li className="is-empty">None tracked in inventory</li>
                      ) : (
                        (dayCloseData.leftovers?.veg || []).map((it) => (
                          <li key={`v-${it.id}`}>
                            <span>{it.name}</span>
                            <strong>{it.stock_qty}</strong>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                  <div className="pos-day-close-leftover-block">
                    <h4>Leftover non-veg snacks</h4>
                    <ul className="pos-day-close-leftovers">
                      {(dayCloseData.leftovers?.non_veg || []).length === 0 ? (
                        <li className="is-empty">None tracked in inventory</li>
                      ) : (
                        (dayCloseData.leftovers?.non_veg || []).map((it) => (
                          <li key={`n-${it.id}`}>
                            <span>{it.name}</span>
                            <strong>{it.stock_qty}</strong>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              ) : null}
              <div className="pos-menu-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={dayCloseSending}
                  onClick={() => setDayCloseOpen(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={dayCloseSending || dayCloseLoading || !dayCloseData}
                  onClick={async () => {
                    setDayCloseSending(true);
                    try {
                      const { data } = await api.post('/pos/day-close', {
                        storeId: parseInt(storeId, 10),
                        sendWhatsApp: true
                      });
                      setMsg({
                        type: 'success',
                        text: data.whatsapp
                          ? 'Day close sent to admin WhatsApp'
                          : 'Day close prepared'
                      });
                      setDayCloseOpen(false);
                    } catch (e) {
                      const text = e.response?.data?.messageText;
                      if (text && navigator.clipboard) {
                        try {
                          await navigator.clipboard.writeText(text);
                        } catch (_) {
                          /* ignore */
                        }
                      }
                      setMsg({
                        type: 'error',
                        text:
                          e.response?.data?.error ||
                          'Could not send WhatsApp. Summary copied if available.'
                      });
                    } finally {
                      setDayCloseSending(false);
                    }
                  }}
                >
                  {dayCloseSending ? 'Sending…' : 'Send to admin WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
